/**
 * Biome system — six rotating scenes (2026-08-01 environment overhaul).
 *
 * Rotation (DESIGN_SYSTEM.md, locked):
 *   city_downtown -> bridge_suspension -> forest_pine -> fields_wheat ->
 *   tunnel -> city_autumn -> repeat (with per-cycle seeded variation).
 *
 * Each biome spans BIOME_LENGTH_M metres (400 m ≈ 15 s mid-run) — exactly
 * CHUNKS_PER_BIOME = 8 road chunks, so a biome boundary ALWAYS falls on a chunk
 * seam and a prop can never straddle one.
 *
 * Two layers of state
 * -------------------
 * 1. GLOBAL (fog colour + near/far, sky gradient, light tint, ground plane
 *    colour/height, road + rail tint). Continuous: lerped from the current
 *    biome to the next across the LAST chunk of the current biome (BLEND_M =
 *    one 50 m chunk). This is the "hard cut at a seam with a one-chunk blend":
 *    by the time the seam reaches the camera the global look has already
 *    arrived, and only the discrete props change at the line itself.
 * 2. PER-CHUNK CONTENT (roadside props). Discrete: each recycled content chunk
 *    is owned by whichever biome its slice falls in, and the six biomes each
 *    pre-build their own content; only the owning biome's is drawn for a given
 *    slot. Swaps happen when a chunk recycles, which is beyond the fog horizon,
 *    so nothing pops.
 *
 *    Two storage shapes since the 2026-08-02 program-count pass: the
 *    NON-INSTANCED per-chunk meshes (kerbs, rails, the tunnel tube, the bridge
 *    cables) still live in per-slot Groups posed every frame, while the
 *    INSTANCED props are ONE mesh per (biome, archetype) with the slot pose
 *    baked into the instance matrices. See the SharedField note below — that
 *    collapse is what took the load screen from ~6.6 s to ~2.5 s.
 *
 * Chunk streaming mirrors the road exactly (see render/roadScroll.ts +
 * render/world.ts): CHUNK_COUNT recycled slots, chunk-local z in
 * [0, CHUNK_LENGTH] running from the far end toward the player, each group
 * posed on the render curve via `chunkPose` (pivoted at the chunk mid, see that
 * function). Placement inside a chunk is chunk-LOCAL and deterministic in the
 * chunk's SLICE INDEX (anchorD / CHUNK_LENGTH) + the run seed, so a recycled
 * slot needs no world bookkeeping and the world never repeats every 8 chunks
 * (the pre-overhaul system keyed placement off the slot index, which made the
 * scenery loop every 400 m).
 *
 * Budget
 * ------
 * Content chunks are also hidden outside a [-SHOW_AHEAD_M, +SHOW_BEHIND_M]
 * window, so only ~4 chunks of props are ever submitted (everything past the
 * window is behind the fog horizon anyway). GLB props are 2.5–8.5k tris each,
 * so hero props are rationed per chunk and density comes from near-free
 * procedural filler (boxes/cones, tens of tris). See the per-biome comments for
 * the visible-triangle arithmetic; the whole biome layer stays under ~100k
 * triangles at any instant on top of the ~126k base scene.
 *
 * Everything render-side. The sim is untouched.
 */

import {
  AdditiveBlending,
  BackSide,
  BoxGeometry,
  BufferGeometry,
  CatmullRomCurve3,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DataTexture,
  DoubleSide,
  Float32BufferAttribute,
  Fog,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  LinearFilter,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshBasicNodeMaterial,
  Object3D,
  PlaneGeometry,
  RingGeometry,
  SRGBColorSpace,
  TubeGeometry,
  Vector3,
  type Material,
  type Scene,
} from "three/webgpu";
import {
  fog as fogNode,
  positionLocal,
  rangeFogFactor,
  reference,
  smoothstep,
  uniform,
} from "three/tsl";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";
import { TUNING } from "../sim/tuning";
import type { World as RoadWorld } from "./world";
import { curveOffset, curveHeading, curveTangent, curveX } from "./curve";
import { citySilhouetteMaterial, plasticMaterial, plasticify } from "./plastic";
import { applyEarthCurve, earthDrop, EYE_HEIGHT, horizonAngle } from "./earthCurve";
import { initialAnchor, recycleAnchor } from "./roadScroll";
import { SHADOW_AHEAD_M, SHADOW_BEHIND_M } from "./riderCaster";

// ---------------------------------------------------------------------------
// Geometry of the streaming window (mirrors the road exactly)
// ---------------------------------------------------------------------------

const R = TUNING.road;
/** Total corridor width (lanes + shoulders) — matches world.ts. */
const CORRIDOR_WIDTH = R.laneCount * R.laneWidth + 2 * R.shoulderWidth;
const CORRIDOR_HALF = CORRIDOR_WIDTH / 2; // 4.3 m
/** Outer face of the guardrail box. */
const RAIL_OUTER = CORRIDOR_HALF + 0.3; // 4.6 m
const CHUNK_LENGTH = R.chunkLength; // 50 m
const CHUNKS_AHEAD = R.chunksAhead;
const CHUNKS_BEHIND = R.chunksBehind;
/**
 * Recycled content-chunk slots: 10 (8 ahead + 2 behind).
 *
 * NOT the same number as CHUNKS_PER_BIOME (8, below) — they are unrelated
 * quantities that happen to be close, and confusing them is what left slots 8
 * and 9 out of the pre-2026-08-02 warm-up (see BiomeSystem.warmup).
 */
const CHUNK_COUNT = CHUNKS_AHEAD + CHUNKS_BEHIND; // 10

/**
 * Hard minimum |x| for ANY biome prop's inner face.
 *
 * The corridor ends at 4.3 m and the rail at 4.6 m, but a rigid 50 m content
 * chunk posed on its mid chord deviates from the true centerline by up to
 * κ·L²/8 ≈ 0.48 m at the shipped curvature (see biomes.geometry.test.ts), so
 * the working clearance is ~0.9 m more than the rail. 6 m is the number the
 * overhaul brief pins and every placement helper below respects it.
 */
export const MIN_PROP_X = 6.0;

/**
 * Content chunks are only submitted inside a window around the camera. The
 * ahead edge is the CURRENT fog far plus this margin — content is culled
 * strictly beyond the point where fog has already swallowed it, so the cull can
 * never pop (a fixed 175 m window popped bridge props in, because that biome's
 * fog reaches 215 m).
 */
const SHOW_AHEAD_MARGIN_M = 45;
const SHOW_BEHIND_M = 70;

// ---------------------------------------------------------------------------
// Biome registry
// ---------------------------------------------------------------------------

export const BIOME_ORDER = [
  "city_downtown",
  "bridge_suspension",
  "forest_pine",
  "fields_wheat",
  "tunnel",
  "city_autumn",
] as const;
export type BiomeId = (typeof BIOME_ORDER)[number];
export const BIOME_COUNT = BIOME_ORDER.length;

/**
 * Metres of road per biome. 400 m ≈ 15 s at mid-run speed (~27 m/s) and is an
 * exact multiple of the 50 m chunk, which is what keeps biome boundaries on
 * chunk seams.
 */
export const BIOME_LENGTH_M = 400;
export const CHUNKS_PER_BIOME = BIOME_LENGTH_M / CHUNK_LENGTH; // 8
export const BIOME_CYCLE_M = BIOME_LENGTH_M * BIOME_COUNT; // 2400
/** Global-state blend window: exactly the seam chunk. */
export const BLEND_M = CHUNK_LENGTH;

/**
 * One biome's global look. Prop content is a separate builder (below); this is
 * everything that is CONTINUOUS and therefore blendable across the seam chunk.
 *
 * All hexes trace back to the DESIGN_SYSTEM palette table.
 */
export interface BiomeDef {
  /** Flat ground plane colour (also the bridge's water). */
  readonly ground: number;
  /** Ground plane height. Below the road for everything but the bridge deck. */
  readonly groundY: number;
  readonly fog: number;
  readonly fogNear: number;
  readonly fogFar: number;
  readonly skyZenith: number;
  readonly skyHorizon: number;
  readonly hemiSky: number;
  readonly hemiGround: number;
  readonly sun: number;
  /**
   * CHEAP GI (2026-08-01 lighting pass). The two colours of the analytic
   * gradient ENVIRONMENT — the upper and lower hemispheres of the light the
   * world sits inside (render/envGradient.ts). This is what the clearcoat lobe
   * samples, so it is what gives a candy vehicle its biome-coloured sheen:
   * warm in the wheat fields, cool green under pines.
   *
   * They are NOT the hemisphere light's colours (which are a broad diffuse
   * top-up) and NOT the sky dome's (which is a backdrop): these are deliberately
   * pulled toward what a surface at road level actually SEES — the sky's
   * horizon band above, the dominant ground/prop colour below — and kept
   * mid-value so the environment stays a soft wraparound and never becomes a
   * second key light.
   */
  readonly envSky: number;
  readonly envGround: number;
  /**
   * LIGHT PROFILE (playtest 2026-08-02: "change the lighting in the tunnel to
   * match a tunnel's lighting"). Multipliers on the three light families of the
   * global rig — key sun, hemisphere fill, and the environment + ground-bounce
   * ambient — applied through `World.setLightProfile` from the same per-frame
   * blend that lerps fog, haze and every colour below, so a profile crossfades
   * across a seam instead of snapping.
   *
   * OMITTED MEANS 1. Every outdoor biome leaves all three out and therefore
   * keeps the 2026-08-01 rig exactly: this feature cannot change the look
   * anywhere but the tunnel. The colours (`hemiSky`, `sun`, `envSky`…) stay
   * where they are — this is the AMOUNT of each light, not its hue.
   */
  readonly sunMul?: number;
  readonly hemiMul?: number;
  readonly envMul?: number;
  /**
   * ROAD STYLE. A subtle per-biome tint of the SHARED asphalt material plus a
   * rail colour swap — geometry, lane markings and lane layout never change
   * (DESIGN_SYSTEM: gameplay readability). Applied by re-tinting the single
   * shared material instances at the blend, never by cloning per chunk.
   */
  readonly asphalt: number;
  readonly rail: number;
}

export const BIOMES: Record<BiomeId, BiomeDef> = {
  // Cream/brick/teal-glass canyon under a bright blue sky.
  city_downtown: {
    ground: 0xa9a49b, // pavement grey
    groundY: -0.06,
    fog: 0xbcc9d8,
    fogNear: 48,
    fogFar: 205,
    skyZenith: 0x4a94e6,
    skyHorizon: 0xbfe3ff,
    hemiSky: 0xbfd0ff,
    hemiGround: 0x6a6258,
    sun: 0xfff4e0,
    envSky: 0xcfe2f5, // pale blue city sky
    envGround: 0x8f8a82, // pavement + concrete bounce
    asphalt: 0xa9aeb6, // the DESIGN_SYSTEM base grey
    rail: 0xe8912c,
  },
  // Red suspension towers over bright blue water.
  bridge_suspension: {
    ground: 0x2ea8d8, // water
    // Water sits just under the deck. Geometry forces this: the camera is
    // 1.35 m over the road and the parapet is 0.9 m at |x| = 4.6 m, so the
    // grazing sight line drops only ~0.1 m per lateral metre — at -9 m the sea
    // was hidden until 100 m out and fogged to white, and at -14 m it was
    // invisible. -1.2 m puts open water in frame a dozen metres past the rail
    // while the deck fascia still reads as a raised deck edge.
    groundY: -1.2,
    fog: 0xaad6ec,
    fogNear: 62,
    fogFar: 210,
    skyZenith: 0x4a94e6,
    skyHorizon: 0xd4f0ff,
    hemiSky: 0xcbe6ff,
    hemiGround: 0x4a7d96,
    sun: 0xfff6e6,
    envSky: 0xd8effc, // open sea sky
    envGround: 0x4e93b4, // the water below the deck — a strong cool bounce
    asphalt: 0xa4abb6, // a hair cooler — sea light
    rail: 0xc8402f, // the bridge's own red: the rail IS the bridge parapet
  },
  // Deep conifer greens, pale mountain backdrop.
  forest_pine: {
    ground: 0x3e6b3a,
    groundY: -0.06,
    fog: 0xc6dbd2,
    fogNear: 46,
    fogFar: 200,
    skyZenith: 0x5a9dea,
    skyHorizon: 0xd5ecff,
    hemiSky: 0xcfe6ff,
    hemiGround: 0x3f5a32,
    sun: 0xfff6dc,
    envSky: 0xd2e8f7,
    envGround: 0x4b6b45, // conifer green — the forest's own bounce
    asphalt: 0xa6abb2,
    rail: 0xe8912c,
  },
  // Warm wheat, low sun, barn red.
  fields_wheat: {
    ground: 0xe8c25a,
    groundY: -0.06,
    fog: 0xedd49c,
    fogNear: 68,
    fogFar: 210,
    skyZenith: 0x63a4e2,
    skyHorizon: 0xffd98f,
    hemiSky: 0xffe9c0,
    hemiGround: 0x9a7c38,
    sun: 0xffedc0,
    envSky: 0xf4dfae, // warm low-sun haze
    envGround: 0xc2a35c, // wheat — the warmest bounce in the game
    asphalt: 0xb0aead, // dusty, warmer
    rail: 0xe8912c,
  },
  // Concrete tube, sodium strips. Dense fog IS the depth falloff.
  tunnel: {
    ground: 0x6a5b4c,
    groundY: -0.06,
    fog: 0x5a4a3c,
    fogNear: 6,
    fogFar: 72,
    // The sky IS the fog colour inside the tube: the only place the
    // background shows is the far end of the visible arch, and a darker sky
    // there read as a black hole punched through the tunnel.
    skyZenith: 0xa08a75,
    skyHorizon: 0xa08a75,
    // SODIUM. The hemisphere is the tunnel's MAIN light (see hemiMul below), so
    // its sky half carries the strip-lamp tone — the same hex the ceiling
    // fixtures and their halos are drawn in, which is what makes the fittings
    // read as the source of the light on the concrete.
    hemiSky: 0xffb46b,
    // The hemisphere's GROUND half is what lights DOWNWARD-facing surfaces —
    // in here that is the vault of the arch, the soffit of every gantry and the
    // underside of each light fitting. Lifted 0x4a3c30 -> 0x7a5f46 with the
    // 2026-08-02 profile: at hemiMul 4.2 the old near-black turned the crown
    // into a void directly above the brightest objects on screen, which is the
    // one place a real tunnel's concrete is visibly lit.
    hemiGround: 0x7a5f46,
    sun: 0xffcf9a,
    // Inside the tube there is no sky: BOTH hemispheres are sodium-lit
    // concrete, which is what stops the tunnel reading as an outdoor road with
    // the lights off.
    envSky: 0x8f7a63,
    envGround: 0x6b5645,
    // LIGHT PROFILE (2026-08-02). The tunnel used to leave the global rig
    // alone: full noon sun through a concrete roof, which threw a hard rider
    // shadow inside a tube and read as "outdoors, but brown". Now:
    //  - sun 12%: not 0. A tunnel is not a cave — light spills in from the
    //    mouths and off the deck, and keeping a TRACE of the key preserves the
    //    hero shadow (render/riderCaster.ts) as the faint smudge a lamp-lit
    //    road actually gives you, rather than deleting the one shadow the
    //    player reads as their own body.
    //  - env/bounce 15%: the gradient environment is a stand-in for open sky.
    //    There is no sky in here.
    //  - hemi 4.2x: the sodium fill BECOMES the key. 0.17 -> 0.71 with the warm
    //    hemiSky above is what puts the light back on the concrete after the
    //    sun and the environment come out, and it is directionless — exactly
    //    how a row of ceiling strips lights a tube.
    sunMul: 0.12,
    hemiMul: 4.2,
    envMul: 0.15,
    // Lighter than the 2026-08-01 value (0x74716c): that darkening existed only
    // to stop the FULL-strength sun blowing the deck out inside the tube. With
    // the profile above the deck is lit by the lamps, so the asphalt can go back
    // to a believable dim concrete grey and take its shading from the light
    // instead of from its albedo.
    asphalt: 0x9a938c,
    rail: 0x9c6220,
  },
  // Brownstone rows, orange canopy, hazy warm sky.
  city_autumn: {
    ground: 0xb9b3a6, // pavement
    groundY: -0.06,
    fog: 0xe8c9a6,
    fogNear: 48,
    fogFar: 205,
    skyZenith: 0x63a0dd,
    skyHorizon: 0xffe0b8,
    hemiSky: 0xffdcb4,
    hemiGround: 0x7a5c3e,
    sun: 0xffe6bc,
    envSky: 0xf0dcc2, // hazy warm sky
    envGround: 0x9a8a76, // pavement under an orange canopy
    asphalt: 0xa8a49c, // warm grey pavement
    rail: 0xe8912c,
  },
};

// ---------------------------------------------------------------------------
// Distance -> biome mapping (pure; unit-tested)
// ---------------------------------------------------------------------------

/** Current biome + the blend toward the next one. */
export interface BiomeBlend {
  /** Index into BIOME_ORDER of the biome the player is in. */
  index: number;
  id: BiomeId;
  /** The biome after `id` (what the blend runs toward). */
  next: BiomeId;
  /** Metres travelled inside `id`, in [0, BIOME_LENGTH_M). */
  local: number;
  /** Blend factor toward `next`: 0 until the last chunk, then 0->1 (eased). */
  t: number;
  /** How many full six-biome cycles have completed (drives per-cycle variation). */
  cycle: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function smoothstep01(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

/** Biome id for a travel distance (the mapper the content chunks use). */
export function biomeIdAt(distance: number): BiomeId {
  return BIOME_ORDER[biomeIndexAt(distance)];
}

/** Index into BIOME_ORDER for a travel distance. Wraps for negatives too. */
export function biomeIndexAt(distance: number): number {
  const total = ((distance % BIOME_CYCLE_M) + BIOME_CYCLE_M) % BIOME_CYCLE_M;
  return Math.floor(total / BIOME_LENGTH_M);
}

/**
 * Biome + blend factor for a travel distance, written into `out` (zero
 * allocation — the update loop calls this every frame).
 *
 * The blend runs over the LAST BLEND_M metres of a biome, i.e. exactly the
 * seam chunk, and is smoothstepped so the fog/sky/tint arrive without a
 * derivative kink at either end.
 */
export function biomeBlendAt(distance: number, out: BiomeBlend): BiomeBlend {
  const total = ((distance % BIOME_CYCLE_M) + BIOME_CYCLE_M) % BIOME_CYCLE_M;
  const index = Math.floor(total / BIOME_LENGTH_M);
  out.index = index;
  out.id = BIOME_ORDER[index];
  out.next = BIOME_ORDER[(index + 1) % BIOME_COUNT];
  out.local = total - index * BIOME_LENGTH_M;
  const blendStart = BIOME_LENGTH_M - BLEND_M;
  out.t = out.local <= blendStart ? 0 : smoothstep01((out.local - blendStart) / BLEND_M);
  out.cycle = Math.floor(distance / BIOME_CYCLE_M);
  return out;
}

/**
 * Index of a chunk INSIDE its biome, 0 .. CHUNKS_PER_BIOME-1, from its slice
 * index (anchorD / CHUNK_LENGTH). Used by content that cares about where in the
 * biome it sits — the tunnel mouths, the bridge tower rhythm.
 */
export function localChunkIndex(slice: number): number {
  const n = CHUNKS_PER_BIOME;
  return ((slice % n) + n) % n;
}

// ---------------------------------------------------------------------------
// Deterministic hashing (run seed + slice index + salt)
// ---------------------------------------------------------------------------

/** Integer hash -> [0,1). Cheap, stable, no allocation. */
function hashInt(a: number): number {
  let x = a | 0;
  x ^= x << 13;
  x = x | 0;
  x ^= x >>> 17;
  x ^= x << 5;
  x = x | 0;
  return (x >>> 0) / 4294967296;
}

/** The run seed, set once by `createBiomeSystem`. Mirrors sim determinism. */
let RUN_SEED = 1;

/**
 * Deterministic [0,1) for (chunk slice, salt). Keyed on the SLICE index (a
 * road-distance address) and the run seed, so the same run always draws the
 * same world and every 400 m cycle gets fresh placement.
 */
function rnd(slice: number, salt: number): number {
  return hashInt(
    Math.imul(slice + 1013, 73856093) ^ Math.imul(salt + 101, 19349663) ^ Math.imul(RUN_SEED, 83492791),
  );
}

/** `rnd` mapped onto [a, b). */
function rndRange(slice: number, salt: number, a: number, b: number): number {
  return a + (b - a) * rnd(slice, salt);
}

// ---------------------------------------------------------------------------
// Off-road ground detail (playtest 2026-08-01)
// ---------------------------------------------------------------------------

/**
 * Owner note the ground work answers: "the ground outside the playable area is
 * a flat single colour and boring". Each biome now lays cheap, chunk-local,
 * DETERMINISTIC detail over the global ground plane — slabs, patches, blobs,
 * seams, kerbs. All of it obeys the same rules as the props:
 *
 *  - flat quads sit a couple of centimetres above the ground plane (never
 *    coplanar with it — that z-fights), chunky boxes/faceted blobs otherwise;
 *  - colours are DESIGN_SYSTEM palette entries or ±10% luminance variants of
 *    the biome's own ground colour, via `shade`;
 *  - placement is `rnd(slice, salt)`, so it is stable per run and per metre of
 *    road rather than per recycled slot;
 *  - it lives in the biome's content chunks, so it streams, recycles and gets
 *    culled by the same visibility window as everything else.
 */

/**
 * A luminance variant of a palette hex: multiplies every channel by `k` and
 * clamps. `k` stays inside [0.9, 1.1] per the brief.
 */
export function shade(hex: number, k: number): number {
  const cl = (v: number): number => Math.max(0, Math.min(255, Math.round(v * k)));
  return (cl((hex >> 16) & 0xff) << 16) | (cl((hex >> 8) & 0xff) << 8) | cl(hex & 0xff);
}

/**
 * Minimum |x| for FLAT, ankle-high ground detail (kerbs, slabs, seams).
 *
 * Lower than MIN_PROP_X (6.0 m) on purpose: that number buys clearance for
 * TALL props, which must never appear to lean over the road when a rigid 50 m
 * content chunk deviates by up to 0.48 m from the true centreline. A 15 cm kerb
 * at 5.5 m swings no closer than 5.0 m — still clear of the rail's outer face
 * at 4.6 m — and being ground-hugging it reads as roadside dressing even at the
 * worst of the bend.
 */
const GROUND_DETAIL_MIN_X = 5.5;

// ---------------------------------------------------------------------------
// Chunk pose (unchanged contract — bends content onto the render curve)
// ---------------------------------------------------------------------------

/** Pose of a content-chunk group on the curve — see `chunkPose`. */
export interface ChunkPose {
  x: number;
  z: number;
  yaw: number;
}

/**
 * World pose for one content chunk, bending it onto the render curve.
 *
 * Chunk-local z runs [0, chunkLength] from the ahead edge toward the player, so
 * the group's ORIGIN is the ahead edge — but the curve is sampled (offset + yaw)
 * at the chunk MID, which is where the straight-chunk approximation is centred
 * (the road ribbon does the same, world.ts poses each sub-segment at its own
 * mid). Rotating about the ahead edge while offsetting by the mid's value left a
 * constant lateral bias of -(chunkLength/2)*sin(yaw) — up to ~3.25 m of scenery
 * sliding off the road edge on bends. So we pivot about the mid explicitly:
 * three.js gives world = R*local + position, and we want the mid
 * (0, 0, chunkLength/2) to land at (curveOffset(mid), 0, mid), hence
 *   position = (offsetMid, 0, midWorldZ) - R*(0, 0, chunkLength/2).
 *
 * Pure arithmetic written into `out`; exported for tests.
 */
export function chunkPose(
  aheadEdgeZ: number,
  distance: number,
  chunkLength: number,
  out: ChunkPose,
): ChunkPose {
  const half = chunkLength / 2;
  const midWorldZ = aheadEdgeZ + half;
  const yaw = curveHeading(midWorldZ, distance);
  out.x = curveOffset(midWorldZ, distance) - Math.sin(yaw) * half;
  out.z = midWorldZ - Math.cos(yaw) * half;
  out.yaw = yaw;
  return out;
}

// ---------------------------------------------------------------------------
// GLB prop templates
// ---------------------------------------------------------------------------

const ENV_MODELS = {
  buildingCream: "/assets/models/env/building_cream.glb",
  buildingBrick: "/assets/models/env/building_brick.glb",
  buildingGlass: "/assets/models/env/building_glass.glb",
  watertower: "/assets/models/env/watertower.glb",
  pineA: "/assets/models/env/pine_a2.glb",
  pineB: "/assets/models/env/pine_b3.glb",
  windmill: "/assets/models/env/windmill.glb",
  barn: "/assets/models/env/barn.glb",
  haybale: "/assets/models/env/haybale.glb",
  brownstone: "/assets/models/env/brownstone_a.glb",
  autumnTree: "/assets/models/env/autumn_tree.glb",
  bridgetower: "/assets/models/env/bridgetower.glb",
} as const;
type EnvKey = keyof typeof ENV_MODELS;

/**
 * Optional albedo multiplier per asset. The generated textures are lit-ish and
 * come out of ACES paler than the palette: the bridge tower in particular read
 * as salmon instead of the locked #c8402f. A colour multiply on the shared
 * material is the cheapest way back onto the palette (the map is preserved).
 */
const ENV_TINT: Partial<Record<EnvKey, number>> = {
  bridgetower: 0xd98a78,
};

/**
 * One loaded environment prop, flattened for instancing.
 *
 * Every env GLB is a SINGLE mesh with a single textured material (see
 * ASSET_PIPELINE "Environment props"), authored bottom-center-origin at a real
 * metre height.
 *
 * `base` is the GLB node's own transform (a uniform scale + lift). It is NOT
 * baked into the geometry: these assets ship KHR_mesh_quantization, so their
 * POSITION attribute is a NORMALISED Int16 buffer — `geometry.applyMatrix4`
 * writes the scaled values straight back into that buffer and clamps every
 * prop to a 2 m normalised cube (which is exactly what it did on the first
 * pass: 54 m bridge towers rendered as 2 m red pebbles). Instead every
 * instance matrix is post-multiplied by `base`, which costs one matrix
 * multiply per placement and keeps the source buffers untouched.
 */
interface PropTemplate {
  geometry: BufferGeometry;
  material: Material;
  base: Matrix4;
  /** Bbox extents in metres (x width, y height, z depth) at scale 1. */
  width: number;
  height: number;
  depth: number;
  /** Triangles per instance — reported for the budget. */
  tris: number;
}
type Templates = Partial<Record<EnvKey, PropTemplate>>;

async function loadEnvTemplates(): Promise<Templates> {
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  const out: Templates = {};
  await Promise.all(
    (Object.keys(ENV_MODELS) as EnvKey[]).map(async (key) => {
      const gltf = await new Promise<GLTF>((resolve, reject) => {
        loader.load(ENV_MODELS[key], resolve, undefined, reject);
      }).catch((err: unknown) => {
        console.warn(`[biomes] env GLB failed (${key}, non-fatal):`, err);
        return null;
      });
      if (!gltf) return;
      // Shared toy-plastic policy; textured materials keep their maps.
      plasticify(gltf.scene);
      gltf.scene.updateWorldMatrix(true, true);
      let found: Mesh | null = null;
      gltf.scene.traverse((o: Object3D) => {
        const m = o as Mesh;
        if (!found && m.isMesh) found = m;
      });
      const mesh = found as Mesh | null;
      if (!mesh) return;
      const geometry = mesh.geometry;
      geometry.computeBoundingBox();
      // Metric extents = the normalised bbox through the node transform.
      const bb = geometry.boundingBox!.clone().applyMatrix4(mesh.matrixWorld);
      const idx = geometry.getIndex();
      const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      const tint = ENV_TINT[key];
      if (tint !== undefined) {
        (material as Material & { color?: Color }).color?.setHex(tint, SRGBColorSpace);
      }
      out[key] = {
        geometry,
        material,
        base: mesh.matrixWorld.clone(),
        width: bb.max.x - bb.min.x,
        height: bb.max.y - bb.min.y,
        depth: bb.max.z - bb.min.z,
        tris: (idx ? idx.count : geometry.getAttribute("position").count) / 3,
      };
    }),
  );
  return out;
}

// ---------------------------------------------------------------------------
// Instanced prop fields — ONE InstancedMesh per (biome, archetype)
// ---------------------------------------------------------------------------

const _dummy = new Object3D();
const _color = new Color();

/**
 * PROGRAM-COUNT PASS (2026-08-02). Why the prop meshes are no longer per chunk.
 *
 * three keys a whole SHADER PROGRAM per InstancedMesh — `getMaterialCacheKey`
 * mixes `object.uuid` into the key for anything instanced, because the instance
 * matrix buffer's node id is baked into the generated WGSL — and generating one
 * costs ~12 ms of JavaScript. With a PropField per (biome, chunk slot,
 * archetype) that was 6 biomes x 10 recycled slots x ~7 archetypes ≈ 413
 * programs ≈ 5 s of pure codegen, and that product WAS the load screen
 * (render/warmup.ts documents the measurement).
 *
 * So the slot axis is gone: there is now exactly ONE InstancedMesh per
 * (biome, archetype), holding CHUNK_COUNT slots' worth of instances, which
 * takes the program count to ~41.
 *
 * THE TRANSFORM TRICK THAT MAKES IT FREE. A content chunk used to be a Group
 * posed every frame by `chunkPose(aheadEdgeZ, distance, …)`. Expand it:
 *
 *     yaw   = curveHeading(mid, D)   = -atan(curveTangent(anchorD + L/2))
 *     pos.x = curveX(anchorD + L/2) - sin(yaw)·L/2  -  curveX(D)
 *     pos.z = -anchorD - L/2 - cos(yaw)·L/2         +  D
 *
 * The left-hand terms depend ONLY on the chunk's road anchor — which changes
 * exactly once per recycle — and the right-hand terms are the SAME for every
 * chunk of every biome. So a chunk's pose factorises into
 *
 *     chunkGroup = T(-curveX(D), 0, D) · slotMatrix(anchorD)
 *
 * `slotMatrix` is baked into the instance matrices at populate time (a single
 * `premultiply` per instance, on the same code path that already writes them)
 * and the global part becomes ONE Group whose position is written once per
 * frame. Identical world matrices, no per-instance per-frame work, and no
 * shader change at all — no `slotIndex` attribute, no uniform array, and in
 * particular NORMALS still take the chunk's yaw the way they always did.
 *
 * DRAW PARITY. The old code called `mesh.count = n` per chunk so only the
 * populated instances of the ~4 chunks inside the visibility window were ever
 * submitted. That still holds: each slot stages its instances into its own
 * region of a CPU-side buffer and `SharedField.apply` COMPACTS the staged
 * regions of exactly the visible slots into the live instance buffer. The
 * shadow-casting slots are compacted FIRST, so `onBeforeShadow` can shrink
 * `count` to them and the shadow pass keeps the tight per-chunk culling the
 * `setCastShadow` toggle used to give it.
 */
const _slotPose: ChunkPose = { x: 0, z: 0, yaw: 0 };
/** Chunk-local -> biome-prop-root transform for each recycled slot. */
const _slotMat: Matrix4[] = Array.from({ length: CHUNK_COUNT }, () => new Matrix4());

/**
 * Re-derive slot `i`'s rigid transform from its road anchor. Call whenever the
 * anchor changes (build + every recycle), BEFORE repopulating that slot.
 *
 * Evaluated at distance 0, which is exactly the factorisation above: the
 * player-distance part of `chunkPose` lives on the shared prop root instead.
 */
function updateSlotMatrix(i: number, anchorD: number): void {
  chunkPose(-anchorD - CHUNK_LENGTH, 0, CHUNK_LENGTH, _slotPose);
  const m = _slotMat[i];
  m.makeRotationY(_slotPose.yaw);
  m.setPosition(_slotPose.x, 0, _slotPose.z);
}

/**
 * ONE InstancedMesh for one (biome, archetype), shared by all CHUNK_COUNT
 * recycled slots. Slots stage into `stageM`/`stageC` at a fixed stride and
 * `apply` compacts the visible ones into the live buffers.
 */
class SharedField {
  readonly mesh: InstancedMesh;
  /** Instances one slot may hold — the old per-chunk PropField capacity. */
  readonly perSlot: number;
  private readonly stageM: Float32Array;
  private readonly stageC: Float32Array | null;
  private readonly counts = new Int32Array(CHUNK_COUNT);
  private dirty = true;
  private lastVis = -1;
  private lastShadow = -1;
  /** Instances drawn in the colour pass / the shadow pass (a prefix of them). */
  private visCount = 0;
  private shadowCount = 0;

  constructor(
    geometry: BufferGeometry,
    material: Material,
    perSlot: number,
    colored: boolean,
    castsShadow: boolean,
  ) {
    this.perSlot = perSlot;
    const capacity = perSlot * CHUNK_COUNT;
    this.stageM = new Float32Array(capacity * 16);
    this.mesh = new InstancedMesh(geometry, material, capacity);
    // Instance matrices carry the whole world pose bar one shared translation,
    // so a per-frame bounding-sphere update would be wasted work; the
    // visibility window (SHOW_AHEAD_M / SHOW_BEHIND_M) does the culling, by
    // deciding which slots get compacted in at all.
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = castsShadow;
    this.mesh.receiveShadow = true;
    this.mesh.count = 0;
    this.mesh.visible = false;
    if (colored) {
      // Warm up the instanceColor buffer so setColorAt is legal from frame 1.
      _color.setRGB(1, 1, 1);
      for (let i = 0; i < capacity; i++) this.mesh.setColorAt(i, _color);
      this.stageC = new Float32Array(capacity * 3).fill(1);
    } else {
      this.stageC = null;
    }
    if (castsShadow) {
      // SHADOW-PASS WINDOW. `apply` lays the shadow-casting slots down first,
      // so the shadow pass is exactly the first `shadowCount` instances. three
      // reads `object.count` inside `renderObject`, which runs between these
      // two hooks (see getShadowRenderObjectFunction), so this is the whole of
      // what the old per-chunk `setCastShadow` toggle bought.
      this.mesh.onBeforeShadow = (): void => {
        this.mesh.count = this.shadowCount;
      };
      this.mesh.onAfterShadow = (): void => {
        this.mesh.count = this.visCount;
      };
    }
  }

  /** Stage instance `i` of slot `slot`. */
  write(slot: number, i: number, matrix: Matrix4, colorHex: number | undefined): void {
    const at = slot * this.perSlot + i;
    matrix.toArray(this.stageM, at * 16);
    if (this.stageC !== null && colorHex !== undefined) {
      _color.setHex(colorHex, SRGBColorSpace);
      this.stageC[at * 3] = _color.r;
      this.stageC[at * 3 + 1] = _color.g;
      this.stageC[at * 3 + 2] = _color.b;
    }
  }

  setCount(slot: number, n: number): void {
    this.counts[slot] = n;
    this.dirty = true;
  }

  /**
   * Compact the staged instances of the slots in `visMask` into the live
   * buffers, shadow casters (`shadowMask`) first. Bit i = recycled slot i.
   */
  apply(visMask: number, shadowMask: number): void {
    if (!this.dirty && visMask === this.lastVis && shadowMask === this.lastShadow) return;
    this.dirty = false;
    this.lastVis = visMask;
    this.lastShadow = shadowMask;
    const dstM = this.mesh.instanceMatrix.array as Float32Array;
    const dstC = this.mesh.instanceColor ? (this.mesh.instanceColor.array as Float32Array) : null;
    let w = 0;
    for (let pass = 0; pass < 2; pass++) {
      const bits = pass === 0 ? visMask & shadowMask : visMask & ~shadowMask;
      for (let i = 0; i < CHUNK_COUNT; i++) {
        if ((bits & (1 << i)) === 0) continue;
        const n = this.counts[i];
        if (n === 0) continue;
        const s = i * this.perSlot;
        dstM.set(this.stageM.subarray(s * 16, (s + n) * 16), w * 16);
        if (dstC !== null && this.stageC !== null) {
          dstC.set(this.stageC.subarray(s * 3, (s + n) * 3), w * 3);
        }
        w += n;
      }
      if (pass === 0) this.shadowCount = w;
    }
    this.visCount = w;
    this.mesh.count = w;
    this.mesh.visible = w > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  /**
   * WARM-UP ONLY. Guarantee this archetype draws at least one instance, so its
   * program + colour/shadow pipelines are built under the loading overlay even
   * when no (slot, slice) the warm-up happens to pose populates it — several
   * fields are behind a `rnd(slice, …)` roll and the run reaches slice numbers
   * the warm-up never sees.
   */
  warmFill(): void {
    let total = 0;
    for (let i = 0; i < CHUNK_COUNT; i++) total += this.counts[i];
    if (total > 0) return;
    _dummy.position.set(0, -400, 0);
    _dummy.rotation.set(0, 0, 0);
    _dummy.scale.set(1, 1, 1);
    _dummy.updateMatrix();
    this.write(0, 0, _dummy.matrix, undefined);
    this.setCount(0, 1);
  }

  dispose(): void {
    this.mesh.dispose();
  }
}

/**
 * The per-(biome) registry of shared fields.
 *
 * A biome's chunk factory runs once per recycled slot and creates its fields in
 * a FIXED order (the conditionals in the builders are all slot-independent), so
 * the n-th `field()` call of every slot resolves to the same SharedField. Slot 0
 * constructs them; slots 1..9 just take a view.
 */
class FieldSet {
  readonly shared: SharedField[] = [];
  private slot = 0;
  private idx = 0;

  beginSlot(i: number): void {
    this.slot = i;
    this.idx = 0;
  }

  field(
    geometry: BufferGeometry,
    material: Material,
    perSlot: number,
    colored = false,
    base: Matrix4 | null = null,
    castsShadow = true,
  ): PropField {
    const k = this.idx++;
    let sh = this.shared[k];
    if (sh === undefined) {
      sh = new SharedField(geometry, material, perSlot, colored, castsShadow);
      this.shared[k] = sh;
    }
    return new PropField(sh, this.slot, base);
  }

  /**
   * A field over a loaded GLB template (carries the node transform).
   *
   * `colored` turns the instanceColor buffer on so the SAME generated asset can
   * be re-instanced at slightly different hue/luminance (the 2026-08-01 filler
   * replacement: the second building row is instances of the hero GLBs, tinted
   * per instance, instead of the old box-with-window-texture blocks). The tint
   * multiplies the asset's baseColor map, so it stays inside the palette.
   */
  glb(tpl: PropTemplate, perSlot: number, colored = false): PropField {
    return this.field(tpl.geometry, tpl.material, perSlot, colored, tpl.base);
  }

  attach(root: Group): void {
    for (const s of this.shared) root.add(s.mesh);
  }

  apply(visMask: number, shadowMask: number): void {
    for (const s of this.shared) s.apply(visMask, shadowMask);
  }

  warmFill(): void {
    for (const s of this.shared) s.warmFill();
  }

  dispose(): void {
    for (const s of this.shared) s.dispose();
  }
}

/**
 * One slot's view of a SharedField: the same `reset()` / `add()` / `flush()`
 * placement primitive every biome builder has always used, redirected into the
 * shared buffers at this slot's offset and pre-multiplied by this slot's pose.
 */
class PropField {
  private n = 0;

  constructor(
    readonly shared: SharedField,
    private readonly slot: number,
    /** GLB node transform applied inside every instance matrix (PropTemplate). */
    private readonly base: Matrix4 | null,
  ) {}

  reset(): void {
    this.n = 0;
  }

  /** Place one instance. Returns false when the pool is full (never grows). */
  add(
    x: number,
    y: number,
    z: number,
    rotY: number,
    sx: number,
    sy = sx,
    sz = sx,
    colorHex?: number,
  ): boolean {
    if (this.n >= this.shared.perSlot) return false;
    _dummy.position.set(x, y, z);
    _dummy.rotation.set(0, rotY, 0);
    _dummy.scale.set(sx, sy, sz);
    _dummy.updateMatrix();
    if (this.base) _dummy.matrix.multiply(this.base);
    // The chunk group's pose, baked in. See the module note above.
    _dummy.matrix.premultiply(_slotMat[this.slot]);
    this.shared.write(this.slot, this.n, _dummy.matrix, colorHex);
    this.n++;
    return true;
  }

  flush(): void {
    this.shared.setCount(this.slot, this.n);
  }
}

/**
 * A subtle per-instance tint around white: `k` is a luminance multiplier and
 * `warm` skews red-vs-blue. Both stay inside ±12%, which is enough to break up
 * a row of identical instances without leaving the asset's own palette.
 */
function instanceTint(k: number, warm: number): number {
  const cl = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return (cl(255 * k * (1 + warm)) << 16) | (cl(255 * k) << 8) | cl(255 * k * (1 - warm));
}

// ---------------------------------------------------------------------------
// Content chunk / biome content contracts
// ---------------------------------------------------------------------------

interface ChunkContent {
  group: Group;
  /**
   * Road-distance anchor of the chunk's near edge (mirrors the road chunks).
   * The chunk covers travel distances [anchorD, anchorD + CHUNK_LENGTH].
   */
  anchorD: number;
  /** (Re)write this chunk's props for the given slice index (anchorD / 50). */
  populate(slice: number): void;
  setVisible(v: boolean): void;
  /**
   * Switch this chunk's non-instanced shadow CASTERS on/off. The sun's ortho
   * frustum only reaches SHADOW_AHEAD_M down the road (render/riderCaster.ts
   * SHADOW_BOX) and every prop mesh has `frustumCulled = false`, so without
   * this the shadow pass would submit every content chunk out to the fog
   * horizon — ~2.5× the geometry it can actually use. The INSTANCED props get
   * the same window from `SharedField.apply` + `onBeforeShadow`.
   */
  setCastShadow(v: boolean): void;
}

interface BiomeContent {
  chunks: ChunkContent[];
  /** This biome's one-mesh-per-archetype instanced prop fields. */
  fields: FieldSet;
  /** Distant silhouette layer (faded in/out with the biome weight). */
  backdrop: Backdrop | null;
  dispose(): void;
}

/** A per-chunk populate function built over a chunk group + the biome's fields. */
type ChunkFactory = (group: Group, F: FieldSet) => (slice: number) => void;

/** Build CHUNK_COUNT recycled chunks from one factory. */
function buildChunks(factory: ChunkFactory, F: FieldSet): ChunkContent[] {
  const chunks: ChunkContent[] = [];
  for (let i = 0; i < CHUNK_COUNT; i++) {
    F.beginSlot(i);
    const group = new Group();
    const populate = factory(group, F);
    // The caster set is decided at BUILD time (each builder flags the meshes
    // that should throw a shadow — flat ground decals, tunnel walls and glow
    // quads deliberately do not) and only toggled afterwards, so a chunk that
    // comes back inside the sun's frustum restores exactly the same set.
    const casters: (Mesh | InstancedMesh)[] = [];
    group.traverse((o: Object3D) => {
      const m = o as Mesh;
      if (m.isMesh && m.castShadow) casters.push(m);
    });
    let casting = true;
    chunks.push({
      group,
      anchorD: initialAnchor(i, CHUNK_LENGTH, CHUNKS_BEHIND),
      populate,
      setVisible: (v: boolean) => {
        group.visible = v;
      },
      setCastShadow: (v: boolean) => {
        if (v === casting) return;
        casting = v;
        for (const m of casters) m.castShadow = v;
      },
    });
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Backdrops — the distant silhouette layer
// ---------------------------------------------------------------------------

/**
 * A flat silhouette card parked ~BACKDROP_DIST m in front of the camera and
 * yawed to face it, so it never slides out of frame on a bend. Unfogged (the
 * pale palette colours ARE the atmosphere) and drawn first with depthWrite off,
 * so every real prop and the ground plane paint over it.
 */
const BACKDROP_DIST = 250;

interface Backdrop {
  group: Group;
  /** 0 = invisible, 1 = fully present. Drives the crossfade at a seam. */
  setWeight(w: number): void;
  dispose(): void;
}

/** Build a silhouette geometry from axis-aligned rectangles in the XY plane. */
function quadsGeometry(rects: readonly (readonly [number, number, number, number])[]): BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  let v = 0;
  for (const [x, y, w, h] of rects) {
    pos.push(x, y, 0, x + w, y, 0, x + w, y + h, 0, x, y + h, 0);
    idx.push(v, v + 1, v + 2, v, v + 2, v + 3);
    v += 4;
  }
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Build a silhouette geometry from explicit triangles (mountains, hills). */
function trisGeometry(tris: readonly (readonly number[])[]): BufferGeometry {
  const pos: number[] = [];
  for (const t of tris) pos.push(t[0], t[1], 0, t[2], t[3], 0, t[4], t[5], 0);
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

function backdropMaterial(color: number): MeshBasicMaterial {
  const m = new MeshBasicMaterial({ color });
  // DoubleSide, deliberately: these are flat cards authored by hand in the XY
  // plane and the triangle helpers wind either way (the first pass lost every
  // mountain ridge to back-face culling while the rectangle skylines showed).
  m.side = DoubleSide;
  m.fog = false;
  m.transparent = true;
  m.depthWrite = false;
  m.toneMapped = true;
  // The silhouette cards are world-space scenery: they take the curved-earth
  // bend (render/earthCurve.ts) like everything else, which is what sinks a
  // distant skyline's feet below the horizon instead of standing it on a line.
  applyEarthCurve(m);
  return m;
}

/** Assemble a backdrop from (geometry, colour) layers, far layer first. */
function makeBackdrop(layers: { geo: BufferGeometry; color: number; y?: number; z?: number }[]): Backdrop {
  const group = new Group();
  group.renderOrder = -10;
  const mats: MeshBasicMaterial[] = [];
  const geos: BufferGeometry[] = [];
  layers.forEach((layer, i) => {
    const mat = backdropMaterial(layer.color);
    const mesh = new Mesh(layer.geo, mat);
    mesh.position.set(0, layer.y ?? 0, layer.z ?? -i * 4);
    mesh.renderOrder = -10 + i;
    mesh.frustumCulled = false;
    group.add(mesh);
    mats.push(mat);
    geos.push(layer.geo);
  });
  return {
    group,
    setWeight(w: number): void {
      group.visible = w > 0.01;
      for (const m of mats) m.opacity = w;
    },
    dispose(): void {
      for (const m of mats) m.dispose();
      for (const g of geos) g.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// Horizon haze band (owner playtest 2026-08-01: "distant objects are grey blobs")
// ---------------------------------------------------------------------------

/**
 * A soft atmospheric band hugging the horizon, tinted with the CURRENT blended
 * fog colour.
 *
 * It is an open-ended cylinder of radius HAZE_RADIUS centred on the player, so
 * it is a fixed distance away in every direction and needs no per-frame pose.
 * Everything nearer than that radius is opaque and wins the depth test, so the
 * band can only ever veil what is BEHIND it — the mid-field (< 120 m) is
 * untouched by construction, which is the brief's hard constraint. It draws
 * after the silhouette backdrops (renderOrder) and over the sky gradient (which
 * is `scene.background`), so the stacking is sky -> skyline -> haze -> world.
 *
 * The band is DELIBERATELY not bent by the curved-earth node: it is a
 * camera-relative atmospheric element, not scenery. Instead it is positioned
 * ANALYTICALLY against the bend — `HAZE_HORIZON_Y` is the world height at which
 * the bent world's visible horizon (earthCurve.ts `horizonAngle`) crosses the
 * cylinder, and the gradient is full-strength from just below there and fades
 * out ~30 m (≈ 9.5° of frame) above it. Below the horizon line the band is
 * hidden behind nearer ground anyway.
 */
const HAZE_RADIUS = 180;
/** Cylinder height (m). Overshoots the visible band at both ends on purpose. */
const HAZE_HEIGHT = 240;
/**
 * Peak opacity at the horizon line. IT MUST BE 1 (2026-08-02 fog-coherence
 * pass; it was 0.85).
 *
 * This band is the ONLY thing that paints the fog colour into the SKY, so its
 * peak opacity is what decides whether a fully fog-saturated object can ever
 * match the sky behind it. At 0.85 the horizon sky settled on
 * 0.85·fog + 0.15·skyHorizon — measured 200,209,218 in downtown against a
 * fully-fogged surface at 204,210,216, i.e. every distant silhouette kept a
 * small but IRREDUCIBLE step (and a blue hue shift) along its outline no matter
 * how much fog it had accumulated. At 1.0 the two are the same pixel by
 * construction — verified: fully-fogged geometry and the horizon sky both read
 * 204,210,216 — so a saturated silhouette has no edge left to see.
 */
const HAZE_PEAK_OPACITY = 1;
/** World y where the bent world's horizon crosses the cylinder — see above. */
const HAZE_HORIZON_Y =
  TUNING.player.cameraHeight - HAZE_RADIUS * Math.tan(horizonAngle());
/** Full strength at/below this y, faded to nothing by HAZE_TOP_Y. */
const HAZE_FULL_Y = HAZE_HORIZON_Y - 1;
/**
 * Top of the fade, as a HEIGHT ABOVE the horizon line at the band radius.
 *
 * 30 m (≈ 9.5° of frame) was too short: the silhouette backdrop cards stand far
 * higher than that — the forest's mountain ridge peaks ~15° above the horizon —
 * so their tops poked out of the veil and cut a hard pale-grey edge into
 * saturated blue sky, which is exactly the shape the owner called a blob. 62 m
 * is ≈ 19°, which covers every backdrop card and every far city mass, and the
 * longer ramp is also a SOFTER one: the haze now thins over ~21° of frame
 * instead of ~12°, so the sky itself reads as atmosphere rather than as a band.
 */
const HAZE_TOP_Y = HAZE_HORIZON_Y + 62;
/**
 * The band is switched off for biomes whose fog never reaches it: inside the
 * tunnel (fogFar 72 m) the tube already encloses the camera, and an UNFOGGED
 * band 180 m away would be a bright ring if any sliver of it showed. Ramped on
 * fog.far so it crossfades with the rest of the global look at a seam.
 */
const HAZE_FOG_FAR_LO = 90;
const HAZE_FOG_FAR_HI = 150;

/**
 * Loose stand-in for a TSL node — the same guard plastic.ts and earthCurve.ts
 * use. Left inferred, the builder generics unify through the whole fluent chain
 * and `tsc --noEmit` blows past 20 GB. Every TSL intermediate below is
 * ANNOTATED with this so the checker stops expanding.
 */
interface TslNode {
  readonly [key: string]: unknown;
  mul(v: unknown): TslNode;
  pow(v: unknown): TslNode;
  oneMinus(): TslNode;
}

/**
 * Extra shaping of the fog TAIL, as an exponent on the remaining transmittance.
 *
 * three's `Fog` already ramps with a smoothstep between near and far
 * (`rangeFogFactor`), which is gentle at both ends — and it is the FAR end that
 * hurt: at the 180 m haze radius the stock curve has only reached 93%, so every
 * distant silhouette still carried 7% of its own (lit, and therefore much
 * brighter than the atmosphere) colour and kept a crisp outline. Re-mapping
 *
 *     f' = 1 - (1 - f)^FOG_TAIL_POWER
 *
 * leaves the near half of the range alone (at 80 m in downtown: 15% vs the
 * stock 10%, both invisible) and drives the last third home — 79% at 142 m,
 * 97% at 174 m, ~100% by 190 m. Distant geometry is therefore FULLY atmosphere
 * before it reaches the haze band, which is what "dissolves softly" means.
 */
const FOG_TAIL_POWER = 1.5;

/**
 * The scene's fog as a TSL node: the stock range fog with the tail above.
 *
 * `Scene.fogNode` takes priority over the node three derives from `scene.fog`
 * (Nodes.getFogNode), and the `reference` nodes below read the LIVE `Fog`
 * object, so every per-biome colour/near/far blend in `update` keeps working
 * untouched — `scene.fog` stays the single source of truth and the content
 * visibility window still keys off `fog.far`.
 */
function saturatingFogNode(f: Fog): Scene["fogNode"] {
  const color = reference("color", "color", f);
  const near = reference("near", "float", f);
  const far = reference("far", "float", f);
  const base = rangeFogFactor(near as never, far as never) as unknown as TslNode;
  const shaped = base.oneMinus().pow(FOG_TAIL_POWER).oneMinus();
  return fogNode(color as never, shaped as never) as unknown as Scene["fogNode"];
}

interface HorizonHaze {
  mesh: Mesh;
  /** Per-frame: the blended fog colour + the blended fog far plane. */
  set(color: Color, fogFar: number): void;
  dispose(): void;
}

function buildHorizonHaze(): HorizonHaze {
  // Open-ended, 64 radial segments: the silhouette is a straight horizontal
  // line, so the only thing the segment count buys is roundness against the
  // 49-54° horizontal FOV — 64 puts a facet every 5.6°, well under a degree of
  // radial error at the frame edges.
  const geo = new CylinderGeometry(HAZE_RADIUS, HAZE_RADIUS, HAZE_HEIGHT, 64, 1, true);
  const mat = new MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    // Seen from the inside.
    side: BackSide,
    toneMapped: true,
  });
  // Tinted per frame from the biome blend; it IS the fog colour, so applying
  // scene fog to it on top would double-count.
  mat.fog = false;
  const weight = uniform(HAZE_PEAK_OPACITY);
  // The mesh sits at the origin and the cylinder is centred, so local y is
  // world y — the gradient can be written directly in world heights.
  const localY: TslNode = positionLocal.y as unknown as TslNode;
  const ramp: TslNode = smoothstep(HAZE_FULL_Y, HAZE_TOP_Y, localY as never) as unknown as TslNode;
  const fade: TslNode = ramp.oneMinus();
  mat.opacityNode = fade.mul(weight) as never;
  const mesh = new Mesh(geo, mat);
  mesh.frustumCulled = false;
  // After the backdrops (-10..-8), before everything else transparent.
  mesh.renderOrder = -5;
  return {
    mesh,
    set(color: Color, fogFar: number): void {
      mat.color.copy(color);
      const w = clamp01((fogFar - HAZE_FOG_FAR_LO) / (HAZE_FOG_FAR_HI - HAZE_FOG_FAR_LO));
      (weight as unknown as { value: number }).value = HAZE_PEAK_OPACITY * smoothstep01(w);
      mesh.visible = w > 0.005;
    },
    dispose(): void {
      geo.dispose();
      mat.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// City biomes (downtown + autumn brownstone)
// ---------------------------------------------------------------------------

/**
 * Shared city layout numbers (2026-08-01 playtest pass).
 *
 * THREE RANKS, the recipe the forest already proved:
 *  - NEAR  — hero GLB buildings, near face at |x| ≈ 8.8-10.5 m;
 *  - MID   — a SECOND ROW of the same generated GLBs, re-instanced at an
 *            independent x/z footprint scale (±25%), a height jitter (≤15%)
 *            and a slight per-instance hue/luminance tint. This replaces the
 *            old procedural box blocks with a fake window texture, which read
 *            as cardboard next to the generated assets;
 *  - FAR   — pure UNTEXTURED silhouette masses: flat single-colour chunky
 *            boxes in the biome's haze tones. No windows, no map. At 30-80 m
 *            behind two rows of real buildings they only ever read as
 *            haze-shapes, and they cost 12 triangles each.
 *
 * Plus a STREET-FURNITURE rank between the rail and the near face, and (autumn)
 * ground cover. `faceMin/faceMax` are the |x| of a building's near FACE, always
 * well clear of MIN_PROP_X.
 */
const CITY = {
  faceMin: 8.8,
  faceMax: 10.5,
  /** Second row of re-instanced hero GLBs. */
  midFaceMin: 14,
  midFaceMax: 23,
  /** Third rank: flat silhouette masses. */
  farFaceMin: 33,
  farFaceMax: 80,
  farPerSide: 5,
  /**
   * Chance a chunk side is a cross-street (no buildings at all). Halved from
   * 0.18 — the owner note was "more building density, fewer gaps"; a cross
   * street every ~10 chunks per side still breaks the wall without leaving the
   * street empty.
   */
  crossStreetChance: 0.09,
} as const;

/**
 * Street furniture — the rank between the guardrail and the building line.
 *
 * All procedural, all chunky palette shapes, NO fake windows: octagonal prisms
 * (bollards + planter boxes) and slab masses (bus shelters, AC units). Every
 * piece is a tall prop, so its INNER FACE respects MIN_PROP_X (6 m), not the
 * flat ground-detail clearance.
 */
const CITY_FURNITURE = {
  bollardsPerSide: 8,
  bollardX: 6.4,
  bollardR: 0.17,
  bollardH: 0.95,
  plantersPerSide: 3,
  planterX: 7.0,
  /** A bus-shelter mass on this fraction of chunk sides. */
  shelterChance: 0.45,
  shelterX: 7.3,
  shelterDepth: 1.8,
  shelterH: 2.6,
  shelterLen: 4.6,
  /** Rooftop AC boxes, per side (hero + mid roofs). */
  acPerSide: 2,
} as const;

/**
 * City pavement: a raised slab band along both shoulders with a kerb lip.
 *
 * The slabs are flat quads with a GAP between them; the gap shows the ground
 * plane underneath, which is what draws the darker seam grid — cheaper and
 * crisper than a seam texture, and it stays sharp at every distance because it
 * is geometry. 2 rows × 20 slabs × 2 sides = 80 quads (160 tris) per chunk.
 */
const CITY_GROUND = {
  kerbX: GROUND_DETAIL_MIN_X,
  kerbW: 0.36,
  kerbH: 0.16,
  /** Inner edge of the slab band (just outside the kerb). */
  slabInner: GROUND_DETAIL_MIN_X + 0.2,
  slabW: 2.0,
  rows: 2,
  slabsPerRow: 20,
  /** Gap between neighbouring slabs — the seam width. */
  seam: 0.13,
  /** Pavement top, relative to the road (the ground plane is at -0.06). */
  top: -0.005,
  /** Fallen-leaf blobs (autumn): flat warm patches, per side per chunk. */
  leaves: 10,
} as const;

interface CityOpts {
  /** GLB hero buildings to draw from — near AND mid rank instance these. */
  heroKeys: EnvKey[];
  /** Flat tints for the far silhouette masses (haze shapes). */
  farTones: number[];
  /** Height range of the far silhouette masses. */
  farHeight: [number, number];
  /** Rooftop water towers on ~40% of hero buildings (downtown only). */
  waterTowers: boolean;
  /**
   * Fraction of chunk sides that carry the MID rank (second building row).
   * Tuned per biome against the triangle budget: downtown mixes three 7.4-8.5k
   * assets, autumn repeats one 6.5k brownstone everywhere, so autumn can carry
   * fewer before it costs more than the forest reference.
   */
  midChance: number;
  /** Street trees, mid rank (autumn only). */
  treeKey?: EnvKey;
  treesPerSide: number;
  /** Fraction of chunk sides that carry street trees. */
  treeChance: number;
  /** Cheap canopy blobs behind the street trees (autumn only). */
  canopyTones?: number[];
  /** Street-furniture tints (planters, shelters, AC boxes). */
  furnitureTones: number[];
  /** Bollards are deliberately NOT accent-coloured — a dark neutral run. */
  bollardTone: number;
  /** Fallen-leaf ground blobs (autumn only). */
  leafTones?: number[];
  /** The biome's ground colour — pavement slabs are luminance variants of it. */
  pavement: number;
}

/**
 * Build a city biome's content.
 *
 * Visible budget (~4 chunks in the window): per side per chunk 1 near hero +
 * 1 mid instance of the same GLB set (7.4-8.5k tris downtown, 6.5k autumn), so
 * ~16 building instances ≈ 120k tris; everything else — 40 far masses, ~90
 * furniture pieces, 160 pavement slabs — is 12-44 triangles apiece and rounds
 * to nothing. The autumn variant swaps ~1-2 GLB trees + canopy blobs per side
 * in and drops the water towers.
 */
function buildCityContent(t: Templates, opts: CityOpts): BiomeContent {
  const heroKeys = opts.heroKeys.filter((k) => t[k]);
  // FAR RANK: untextured silhouette masses. One shared unit box, flat colour,
  // per-instance tint. Deliberately mapless — this is the rank the old
  // window-texture fillers used to occupy.
  const massGeo = new BoxGeometry(1, 1, 1);
  // ONE shared material for the whole rank (both city biomes build through
  // here), tinted per instance via instanceColor. Its window grid fades to flat
  // colour by 140 m — see `citySilhouetteMaterial`.
  const massMat = citySilhouetteMaterial();
  // STREET FURNITURE: an octagonal prism (bollards + planters — "chunky
  // beveled", per the geometry rules) and a slab box (shelters, AC units).
  const prismGeo = new CylinderGeometry(1, 1, 1, 8);
  prismGeo.rotateY(Math.PI / 8);
  prismGeo.translate(0, 0.5, 0);
  const prismMat = plasticMaterial({ color: 0xffffff, name: "cityFurniture" });
  const slabBoxGeo = new BoxGeometry(1, 1, 1);
  slabBoxGeo.translate(0, 0.5, 0);
  const slabBoxMat = plasticMaterial({ color: 0xffffff, name: "cityFurnitureBox" });
  // Pavement: slab quads (tinted per instance) + a solid kerb rail per side.
  const slabGeo = new PlaneGeometry(1, 1);
  slabGeo.rotateX(-Math.PI / 2);
  const slabMat = plasticMaterial({ color: 0xffffff, name: "citySlab" });
  // 10 z-segments, not 1: a rigid 50 m box would chord ACROSS the world bend
  // and sag CHUNK_LENGTH^2/(8R) = 14 cm in its middle - most of a 16 cm kerb.
  // See earthCurve.ts (TESSELLATION).
  const kerbGeo = new BoxGeometry(CITY_GROUND.kerbW, CITY_GROUND.kerbH, CHUNK_LENGTH, 1, 1, 10);
  const kerbMat = plasticMaterial({ color: shade(opts.pavement, 0.92), name: "cityKerb" });
  // Ground cover: flat leaf blobs (autumn) + faceted canopy blobs behind the
  // street trees. Both are the forest's ground-cover recipe, re-tinted.
  const leafGeo = new CircleGeometry(1, 7);
  leafGeo.rotateX(-Math.PI / 2);
  const leafMat = plasticMaterial({ color: 0xffffff, name: "cityLeaf" });
  const canopyGeo = new IcosahedronGeometry(1, 1);
  const canopyMat = plasticMaterial({ color: 0xffffff, name: "cityCanopy" });
  const canopyTrunkGeo = new CylinderGeometry(0.14, 0.2, 1, 5);
  canopyTrunkGeo.translate(0, 0.5, 0);
  const canopyTrunkMat = plasticMaterial({ color: 0x6b4a36, name: "cityCanopyTrunk" });
  const slabTones = [
    shade(opts.pavement, 1.08),
    shade(opts.pavement, 1.02),
    shade(opts.pavement, 0.96),
  ];
  const slabLen = CHUNK_LENGTH / CITY_GROUND.slabsPerRow;
  const owned: (BufferGeometry | Material)[] = [
    massGeo,
    massMat,
    prismGeo,
    prismMat,
    slabBoxGeo,
    slabBoxMat,
    slabGeo,
    slabMat,
    kerbGeo,
    kerbMat,
    leafGeo,
    leafMat,
    canopyGeo,
    canopyMat,
    canopyTrunkGeo,
    canopyTrunkMat,
  ];
  const fieldSet = new FieldSet();
  const CANOPY = opts.canopyTones ? 8 : 0;

  const factory: ChunkFactory = (group, F) => {
    // 2 instances per side (near + mid) can all land on the same asset.
    const heroFields = heroKeys.map((k) => ({ key: k, tpl: t[k]!, field: F.glb(t[k]!, 4, true) }));
    // The far rank is OUT of the shadow pass entirely, casting AND receiving.
    // These are 10-26 m wide boxes 30-80 m off the road: at that size the sun's
    // ortho shadow map has no texel density left, and the first pass painted a
    // dark diagonal wedge of acne across every one of them. They are haze
    // shapes — flat lit colour is exactly what they should be.
    const masses = F.field(massGeo, massMat, CITY.farPerSide * 2, true, null, false);
    masses.shared.mesh.receiveShadow = false;
    const prisms = F.field(
      prismGeo,
      prismMat,
      (CITY_FURNITURE.bollardsPerSide + CITY_FURNITURE.plantersPerSide) * 2,
      true,
    );
    const boxes = F.field(slabBoxGeo, slabBoxMat, (1 + CITY_FURNITURE.acPerSide) * 4, true);
    const tower = opts.waterTowers && t.watertower ? F.glb(t.watertower, 4) : null;
    const trees = opts.treeKey && t[opts.treeKey]
      ? F.glb(t[opts.treeKey]!, opts.treesPerSide * 2 + 2, true)
      : null;
    const leaves = opts.leafTones
      ? F.field(leafGeo, leafMat, CITY_GROUND.leaves * 2, true, null, false)
      : null;
    const canopies = CANOPY > 0 ? F.field(canopyGeo, canopyMat, CANOPY * 2, true) : null;
    const canopyTrunks = CANOPY > 0 ? F.field(canopyTrunkGeo, canopyTrunkMat, CANOPY * 2) : null;

    // Pavement. The kerb is static per chunk (a straight run down the whole
    // slice); only the slab tints are drawn from the slice hash.
    const slabs = F.field(
      slabGeo,
      slabMat,
      CITY_GROUND.rows * CITY_GROUND.slabsPerRow * 2,
      true,
    );
    for (const sideSign of [-1, 1]) {
      const kerb = new Mesh(kerbGeo, kerbMat);
      kerb.position.set(
        sideSign * CITY_GROUND.kerbX,
        CITY_GROUND.top - CITY_GROUND.kerbH / 2,
        CHUNK_LENGTH / 2,
      );
      kerb.frustumCulled = false;
      kerb.receiveShadow = true;
      group.add(kerb);
    }

    return (slice: number): void => {
      for (const h of heroFields) h.field.reset();
      masses.reset();
      prisms.reset();
      boxes.reset();
      tower?.reset();
      trees?.reset();
      leaves?.reset();
      canopies?.reset();
      canopyTrunks?.reset();
      slabs.reset();

      // --- Pavement slab grid (both sides, unconditional) ------------------
      for (const sideSign of [-1, 1]) {
        const s = sideSign > 0 ? 0 : 97;
        for (let row = 0; row < CITY_GROUND.rows; row++) {
          const x = sideSign * (CITY_GROUND.slabInner + (row + 0.5) * CITY_GROUND.slabW);
          for (let i = 0; i < CITY_GROUND.slabsPerRow; i++) {
            const tone =
              slabTones[Math.floor(rnd(slice, s + 600 + row * 41 + i) * slabTones.length)];
            slabs.add(
              x,
              CITY_GROUND.top,
              (i + 0.5) * slabLen,
              0,
              CITY_GROUND.slabW - CITY_GROUND.seam,
              1,
              slabLen - CITY_GROUND.seam,
              tone,
            );
          }
        }
      }

      // --- Fallen leaves (autumn ground cover) -----------------------------
      if (leaves && opts.leafTones) {
        for (const sideSign of [-1, 1]) {
          const s = sideSign > 0 ? 0 : 97;
          for (let i = 0; i < CITY_GROUND.leaves; i++) {
            const rad = rndRange(slice, s + 1200 + i, 0.7, 2.6);
            leaves.add(
              sideSign * rndRange(slice, s + 1230 + i, GROUND_DETAIL_MIN_X + 0.4, 24),
              // Above the pavement top (-0.005) so a blob on the kerb band
              // never z-fights a slab, and each blob gets its own millimetre
              // of height so two OVERLAPPING blobs do not z-fight each other
              // either (they did: coplanar patches striped where they crossed).
              0.012 + i * 0.0009,
              rndRange(slice, s + 1260 + i, -2, CHUNK_LENGTH + 2),
              rndRange(slice, s + 1290 + i, 0, Math.PI),
              rad,
              1,
              rad * rndRange(slice, s + 1320 + i, 0.55, 1.4),
              opts.leafTones[Math.floor(rnd(slice, s + 1350 + i) * opts.leafTones.length)],
            );
          }
        }
      }

      // --- Street furniture (both sides, unconditional) --------------------
      // Independent of the cross-street roll: a cross street still has kerbs,
      // bollards and a planter, which is what keeps the rank continuous.
      for (const sideSign of [-1, 1]) {
        const s = sideSign > 0 ? 0 : 97;
        for (let i = 0; i < CITY_FURNITURE.bollardsPerSide; i++) {
          const h = CITY_FURNITURE.bollardH * rndRange(slice, s + 1400 + i, 0.9, 1.1);
          prisms.add(
            sideSign * CITY_FURNITURE.bollardX,
            0,
            (i + 0.5) * (CHUNK_LENGTH / CITY_FURNITURE.bollardsPerSide),
            0,
            CITY_FURNITURE.bollardR,
            h,
            CITY_FURNITURE.bollardR,
            i % 3 === 1 ? shade(opts.bollardTone, 1.18) : opts.bollardTone,
          );
        }
        for (let i = 0; i < CITY_FURNITURE.plantersPerSide; i++) {
          if (rnd(slice, s + 1460 + i) < 0.25) continue;
          const rad = rndRange(slice, s + 1470 + i, 0.55, 0.85);
          prisms.add(
            sideSign * CITY_FURNITURE.planterX,
            0,
            (i + 0.5) * (CHUNK_LENGTH / CITY_FURNITURE.plantersPerSide) +
              rndRange(slice, s + 1480 + i, -5, 5),
            rndRange(slice, s + 1490 + i, 0, Math.PI),
            rad,
            rndRange(slice, s + 1500 + i, 0.6, 0.95),
            rad,
            opts.furnitureTones[
              Math.floor(rnd(slice, s + 1510 + i) * opts.furnitureTones.length)
            ],
          );
        }
        if (rnd(slice, s + 1520) < CITY_FURNITURE.shelterChance) {
          boxes.add(
            sideSign * CITY_FURNITURE.shelterX,
            0,
            rndRange(slice, s + 1530, CITY_FURNITURE.shelterLen, CHUNK_LENGTH - CITY_FURNITURE.shelterLen),
            0,
            CITY_FURNITURE.shelterDepth,
            CITY_FURNITURE.shelterH * rndRange(slice, s + 1540, 0.92, 1.08),
            CITY_FURNITURE.shelterLen,
            opts.furnitureTones[
              Math.floor(rnd(slice, s + 1550) * opts.furnitureTones.length)
            ],
          );
        }
      }

      for (const sideSign of [-1, 1]) {
        const s = sideSign > 0 ? 0 : 500;

        // --- Far rank: untextured silhouette masses ------------------------
        // Unconditional (a cross street only opens the near two rows) — this
        // is the layer that keeps the skyline unbroken behind a gap.
        for (let i = 0; i < CITY.farPerSide; i++) {
          const salt = s + 1000 + i * 11;
          const w = rndRange(slice, salt + 1, 10, 26);
          const d = rndRange(slice, salt + 2, 10, 24);
          const hgt = rndRange(slice, salt + 3, opts.farHeight[0], opts.farHeight[1]);
          const face = rndRange(slice, salt + 4, CITY.farFaceMin, CITY.farFaceMax);
          masses.add(
            sideSign * (face + d / 2),
            hgt / 2,
            (i + 0.5) * (CHUNK_LENGTH / CITY.farPerSide) + rndRange(slice, salt + 5, -8, 8),
            rndRange(slice, salt + 6, -0.25, 0.25),
            d,
            hgt,
            w,
            opts.farTones[Math.floor(rnd(slice, salt + 7) * opts.farTones.length)],
          );
        }

        if (rnd(slice, s + 1) < CITY.crossStreetChance) continue; // cross street

        // --- NEAR + MID ranks: hero GLB, then a second row of instances ----
        if (heroFields.length > 0) {
          for (let rank = 0; rank < 2; rank++) {
            // The mid rank is dense but not unconditional: 55% of sides carry
            // a second row. The visibility window holds ~6 chunks at the city
            // fog far (205 m + 45 margin), so every +0.1 of this probability is
            // ~1.2 more 6.5-8.5k-triangle GLB instances on screen.
            if (rank === 1 && rnd(slice, s + 199) > opts.midChance) continue;
            const rs = s + rank * 200;
            const pick = Math.min(
              heroFields.length - 1,
              Math.floor(rnd(slice, rs + 2) * heroFields.length),
            );
            const h = heroFields[pick];
            // Footprint varies in x AND z (±25% on the mid rank, never a
            // y-only stretch above 15%).
            const spread = rank === 0 ? 0.2 : 0.25;
            const sx = rndRange(slice, rs + 3, 1 - spread, 1 + spread);
            const sz = rndRange(slice, rs + 4, 1 - spread, 1 + spread);
            const sy = rndRange(slice, rs + 5, 0.85, 1.15);
            // Face the road with the LONG side (a street wall, not a gable
            // end): quarter-turn only when the asset is wider than it is deep.
            // The extra half turn on one side mirrors the row so both kerbs
            // show the modelled facade rather than the plain back slab.
            const quarter = h.tpl.width > h.tpl.depth ? Math.PI * 0.5 : 0;
            const rotY =
              quarter + (sideSign > 0 ? Math.PI : 0) + rndRange(slice, rs + 6, -0.1, 0.1);
            const alongX = quarter !== 0 ? h.tpl.depth * sz : h.tpl.width * sx;
            const alongZ = quarter !== 0 ? h.tpl.width * sx : h.tpl.depth * sz;
            const face =
              rank === 0
                ? rndRange(slice, rs + 7, CITY.faceMin, CITY.faceMax)
                : rndRange(slice, rs + 7, CITY.midFaceMin, CITY.midFaceMax);
            const x = sideSign * (face + alongX / 2);
            // Keep the footprint inside the chunk so a prop never straddles a
            // seam. The two ranks draw from different salts, so the second row
            // interlocks with the first instead of lining up behind it.
            const halfZ = alongZ / 2;
            const z = rndRange(slice, rs + 8, halfZ + 2, CHUNK_LENGTH - halfZ - 2);
            // Slight hue/luminance variation so a repeated asset does not read
            // as a copy-paste. Kept TIGHT (-10%/+2% luminance, ±3% warm/cool):
            // a wider warm skew pushed the saturated brick asset to pink.
            const tint =
              rank === 0
                ? 0xffffff
                : instanceTint(
                    rndRange(slice, rs + 9, 0.9, 1.02),
                    rndRange(slice, rs + 10, -0.03, 0.03),
                  );
            h.field.add(x, 0, z, rotY, sx, sy, sz, tint);

            const topY = h.tpl.height * sy;
            if (tower && rank === 0 && rnd(slice, rs + 11) < 0.4) {
              tower.add(
                x + rndRange(slice, rs + 12, -1.5, 1.5),
                topY - 0.05,
                z + rndRange(slice, rs + 13, -2.5, 2.5),
                rndRange(slice, rs + 14, 0, Math.PI * 2),
                rndRange(slice, rs + 15, 0.85, 1.15),
              );
            }
            // Rooftop AC boxes — the DESIGN_SYSTEM "rooftop props on ~40% of
            // city buildings" line, and cheap enough for every rank.
            for (let a = 0; a < CITY_FURNITURE.acPerSide; a++) {
              if (rnd(slice, rs + 20 + a) < 0.5) continue;
              const aw = rndRange(slice, rs + 30 + a, 1.1, 2.4);
              boxes.add(
                x + rndRange(slice, rs + 40 + a, -2.5, 2.5),
                topY - 0.04,
                z + rndRange(slice, rs + 50 + a, -3, 3),
                rndRange(slice, rs + 60 + a, 0, Math.PI),
                aw,
                rndRange(slice, rs + 70 + a, 0.6, 1.3),
                aw * rndRange(slice, rs + 80 + a, 0.7, 1.4),
                opts.furnitureTones[
                  Math.floor(rnd(slice, rs + 90 + a) * opts.furnitureTones.length)
                ],
              );
            }
          }
        }

        // --- Street trees + canopy blobs (autumn mid rank) -----------------
        if (trees && rnd(slice, s + 39) < opts.treeChance) {
          for (let i = 0; i < opts.treesPerSide; i++) {
            trees.add(
              sideSign * rndRange(slice, s + 41 + i * 6, MIN_PROP_X + 0.7, MIN_PROP_X + 2.2),
              0,
              (i + 0.5) * (CHUNK_LENGTH / Math.max(1, opts.treesPerSide)) +
                rndRange(slice, s + 42 + i * 6, -6, 6),
              rndRange(slice, s + 43 + i * 6, 0, Math.PI * 2),
              rndRange(slice, s + 44 + i * 6, 0.85, 1.3),
              undefined,
              undefined,
              instanceTint(
                rndRange(slice, s + 45 + i * 6, 0.9, 1.08),
                rndRange(slice, s + 46 + i * 6, -0.05, 0.05),
              ),
            );
          }
        }
        if (canopies && canopyTrunks && opts.canopyTones) {
          for (let i = 0; i < CANOPY; i++) {
            // Between the hero face and the mid rank: a clean 10-13.5 m band,
            // so a canopy never grows out of a building.
            const x = sideSign * rndRange(slice, s + 1600 + i, 10.6, 13.6);
            const z = rndRange(slice, s + 1620 + i, -2, CHUNK_LENGTH + 2);
            const rad = rndRange(slice, s + 1640 + i, 1.0, 1.9);
            const trunkH = rndRange(slice, s + 1660 + i, 2.8, 4.4);
            canopies.add(
              x,
              trunkH + rad * 0.55,
              z,
              rndRange(slice, s + 1680 + i, 0, Math.PI * 2),
              rad,
              rad * rndRange(slice, s + 1700 + i, 0.8, 1.15),
              rad * rndRange(slice, s + 1720 + i, 0.85, 1.2),
              opts.canopyTones[Math.floor(rnd(slice, s + 1740 + i) * opts.canopyTones.length)],
            );
            canopyTrunks.add(x, 0, z, 0, 1, trunkH + rad * 0.4, 1);
          }
        }
      }

      for (const h of heroFields) h.field.flush();
      masses.flush();
      prisms.flush();
      boxes.flush();
      tower?.flush();
      trees?.flush();
      leaves?.flush();
      canopies?.flush();
      canopyTrunks?.flush();
      slabs.flush();
    };
  };

  return {
    chunks: buildChunks(factory, fieldSet),
    fields: fieldSet,
    backdrop: null, // assigned by the caller (downtown/autumn differ)
    dispose(): void {
      for (const o of owned) o.dispose();
      fieldSet.dispose();
    },
  };
}

/** Downtown skyline: two haze layers of blocky towers. */
function downtownBackdrop(): Backdrop {
  const far: [number, number, number, number][] = [];
  const near: [number, number, number, number][] = [];
  for (let i = 0; i < 46; i++) {
    const x = -460 + i * 20;
    far.push([x, 0, 18 + hashInt(i * 17 + 1) * 8, 45 + hashInt(i * 29 + 5) * 75]);
  }
  for (let i = 0; i < 30; i++) {
    const x = -420 + i * 28;
    near.push([x, 0, 20 + hashInt(i * 13 + 7) * 12, 30 + hashInt(i * 41 + 3) * 60]);
  }
  return makeBackdrop([
    { geo: quadsGeometry(far), color: 0xb6cbe0 },
    { geo: quadsGeometry(near), color: 0x93b0cd, z: 6 },
  ]);
}

/** Autumn city: the same idea in warm haze. */
function autumnBackdrop(): Backdrop {
  const far: [number, number, number, number][] = [];
  for (let i = 0; i < 40; i++) {
    const x = -440 + i * 22;
    far.push([x, 0, 16 + hashInt(i * 23 + 9) * 10, 28 + hashInt(i * 37 + 11) * 45]);
  }
  const trees: [number, number, number, number][] = [];
  for (let i = 0; i < 44; i++) {
    const x = -440 + i * 20;
    trees.push([x, 0, 14 + hashInt(i * 19 + 2) * 6, 14 + hashInt(i * 43 + 6) * 12]);
  }
  return makeBackdrop([
    { geo: quadsGeometry(far), color: 0xd8c2ac },
    { geo: quadsGeometry(trees), color: 0xdb9a55, z: 6 },
  ]);
}

// ---------------------------------------------------------------------------
// Bridge biome
// ---------------------------------------------------------------------------

/**
 * Uniform scale applied to bridgetower.glb.
 *
 * The asset is a 13.42 m wide portal whose LEG INNER FACES sit at |x| = 3.96 m
 * at native scale — inside the corridor. Scaling it up is the only way to make
 * the portal straddle the road: 1.55 puts the inner faces at 6.14 m, clearing
 * MIN_PROP_X (and therefore the rail plus the worst-case per-chunk curve
 * shift), and takes the towers to 54 m, which is the right drama for the
 * reference frame's suspension bridge.
 */
const BRIDGE_TOWER_SCALE = 1.55;
const BRIDGE_TOWER_NATIVE_INNER_X = 3.96;
const BRIDGE_TOWER_NATIVE_HALF_X = 6.71;
/** Cable/suspender line: the middle of a tower leg. */
const BRIDGE_CABLE_X =
  ((BRIDGE_TOWER_NATIVE_INNER_X + BRIDGE_TOWER_NATIVE_HALF_X) / 2) * BRIDGE_TOWER_SCALE; // ≈ 8.3 m
const BRIDGE_TOWER_TOP = 35 * BRIDGE_TOWER_SCALE; // ≈ 54.25 m
const BRIDGE_CABLE_TOP = BRIDGE_TOWER_TOP - 6;
const BRIDGE_CABLE_SAG = 9;
const BRIDGE_DECK_DROP = 1.1;

/**
 * THE SPAN LAYOUT (2026-08-01 playtest: "the bridge ends abruptly with the
 * suspension wire floating in the air").
 *
 * Everything below is in BIOME-LOCAL metres, `bz` ∈ [0, BIOME_LENGTH_M]: bz = 0
 * is the seam the player enters the bridge on, bz = 400 the seam they leave it
 * on. A chunk whose `localChunkIndex` is L covers bz ∈ [L·50, (L+1)·50], and
 * chunk-local z runs the OTHER way (z = 0 is the far edge), so
 *   bz = (L + 1)·50 − z.
 *
 * The span is a Golden-Gate arrangement: an ANCHORAGE at each end, a straight
 * back-stay from the anchorage up to the nearest tower, then two 100 m main
 * spans between three towers. The cable therefore terminates INSIDE a chunky
 * beveled abutment mass at deck level at both ends — it can never end in
 * mid-air, in any frame, including the seam chunks where the neighbouring
 * biome is on screen. Beyond the anchorages (bz < 5 and bz > 345) there is no
 * cable at all: that is the approach viaduct, and it is what lets the biome
 * hand over to its neighbour without a dangling wire.
 *
 * Everything here is a pure function of `localChunkIndex`, so cycle 2+ is
 * bit-identical to cycle 1 (see biomes.geometry.test.ts).
 */
export const BRIDGE_SPAN = {
  /** Biome-local z of the three portal towers. */
  towers: [75, 175, 275] as const,
  /** Biome-local z where the main cable dies into the anchorage. */
  anchorStart: 5,
  anchorEnd: 345,
  /** Height the cable enters the abutment at (= the abutment's cap top). */
  anchorY: 2.6,
  /** Distance between neighbouring towers — the main-span sag scale. */
  span: 100,
} as const;

/** Chunk-local index that carries each tower / abutment. */
const BRIDGE_TOWER_CHUNKS = BRIDGE_SPAN.towers.map((bz) => Math.floor(bz / CHUNK_LENGTH));
const BRIDGE_ANCHOR_CHUNKS = [
  Math.floor(BRIDGE_SPAN.anchorStart / CHUNK_LENGTH),
  Math.floor(BRIDGE_SPAN.anchorEnd / CHUNK_LENGTH),
] as const;

/**
 * Deck expansion joints per chunk. 4 over 50 m = one every 12.5 m — the brief's
 * "~15 m", rounded to a divisor of the chunk so the rhythm is unbroken across a
 * seam instead of stuttering every 50 m.
 */
const BRIDGE_JOINTS_PER_CHUNK = 4;
/** Suspenders per chunk inside a main span. 10 over 50 m = one every 5 m. */
const BRIDGE_SUSPENDERS_PER_CHUNK = 10;

/** Exported so the clearance tests can pin the portal against the corridor. */
export const BRIDGE_CLEARANCE = {
  corridorHalf: CORRIDOR_HALF,
  railOuter: RAIL_OUTER,
  /** |x| of the inner face of a scaled tower leg. */
  towerInnerX: BRIDGE_TOWER_NATIVE_INNER_X * BRIDGE_TOWER_SCALE,
  cableX: BRIDGE_CABLE_X,
  /** |x| of the inner face of an end abutment. */
  abutmentInnerX: 9.1 - 5.6 / 2,
} as const;

/**
 * Main-cable height at a BIOME-LOCAL z, or `null` where there is no cable
 * (outside the two anchorages). Pure; exercised by the geometry tests.
 *
 * - inside a main span: a parabolic approximation of a catenary, top at the
 *   towers, `BRIDGE_CABLE_SAG` at mid-span;
 * - between an anchorage and the outermost tower: a STRAIGHT back-stay, which
 *   is what a real suspension bridge does and what makes the termination read
 *   as "anchored" rather than "cut off".
 */
export function bridgeCableY(bz: number): number | null {
  const T = BRIDGE_SPAN.towers;
  if (bz < BRIDGE_SPAN.anchorStart || bz > BRIDGE_SPAN.anchorEnd) return null;
  if (bz <= T[0]) {
    const u = (bz - BRIDGE_SPAN.anchorStart) / (T[0] - BRIDGE_SPAN.anchorStart);
    return lerp(BRIDGE_SPAN.anchorY, BRIDGE_CABLE_TOP, u);
  }
  if (bz >= T[T.length - 1]) {
    const u = (bz - T[T.length - 1]) / (BRIDGE_SPAN.anchorEnd - T[T.length - 1]);
    return lerp(BRIDGE_CABLE_TOP, BRIDGE_SPAN.anchorY, u);
  }
  for (let i = 0; i < T.length - 1; i++) {
    if (bz >= T[i] && bz <= T[i + 1]) {
      const u = (bz - T[i]) / (T[i + 1] - T[i]);
      return BRIDGE_CABLE_TOP - (BRIDGE_CABLE_TOP - BRIDGE_CABLE_SAG) * 4 * u * (1 - u);
    }
  }
  return BRIDGE_CABLE_TOP;
}

/** Chunk-local z -> biome-local z for a chunk at local index `local`. */
function bridgeBz(local: number, zLocal: number): number {
  return (local + 1) * CHUNK_LENGTH - zLocal;
}

/**
 * The chunk-local z range over which chunk `local` carries cable, or null when
 * it carries none (the approach chunks past the anchorages).
 */
function bridgeCableRange(local: number): [number, number] | null {
  const bzFar = bridgeBz(local, 0); // z = 0
  const bzNear = bridgeBz(local, CHUNK_LENGTH); // z = CHUNK_LENGTH
  const lo = Math.max(bzNear, BRIDGE_SPAN.anchorStart);
  const hi = Math.min(bzFar, BRIDGE_SPAN.anchorEnd);
  if (hi - lo < 0.5) return null;
  // Back to chunk-local z (the map is decreasing, so hi/lo swap).
  return [(local + 1) * CHUNK_LENGTH - hi, (local + 1) * CHUNK_LENGTH - lo];
}

/** Main cable tube for ONE chunk at a given local index, or null. */
function buildCableGeometry(sideSign: number, local: number): BufferGeometry | null {
  const range = bridgeCableRange(local);
  if (!range) return null;
  const [z0, z1] = range;
  const pts: Vector3[] = [];
  const samples = 16;
  for (let i = 0; i <= samples; i++) {
    const z = z0 + (i / samples) * (z1 - z0);
    const y = bridgeCableY(bridgeBz(local, z));
    pts.push(new Vector3(sideSign * BRIDGE_CABLE_X, y ?? BRIDGE_SPAN.anchorY, z));
  }
  return new TubeGeometry(new CatmullRomCurve3(pts), samples, 0.28, 6, false);
}

/**
 * Vertical suspenders from the main cable down to deck height, merged.
 *
 * Only inside a MAIN span — a back-stay carries no hangers, which is precisely
 * the cue that reads as "this run is anchored, not floating".
 */
function buildSuspenderGeometry(sideSign: number, local: number): BufferGeometry | null {
  const x = sideSign * BRIDGE_CABLE_X;
  const positions: number[] = [];
  const indices: number[] = [];
  const ring = 4;
  const rad = 0.075;
  let v = 0;
  const first = BRIDGE_SPAN.towers[0];
  const last = BRIDGE_SPAN.towers[BRIDGE_SPAN.towers.length - 1];
  for (let i = 0; i < BRIDGE_SUSPENDERS_PER_CHUNK; i++) {
    const z = ((i + 0.5) / BRIDGE_SUSPENDERS_PER_CHUNK) * CHUNK_LENGTH;
    const bz = bridgeBz(local, z);
    if (bz < first + 3 || bz > last - 3) continue;
    const top = bridgeCableY(bz);
    if (top === null) continue;
    // Lands INSIDE the edge girder box (BRIDGE_GIRDER.topY), never in mid-air.
    const bot = BRIDGE_SUSPENDER_BOT;
    for (const y of [top, bot]) {
      for (let r = 0; r < ring; r++) {
        const a = (r / ring) * Math.PI * 2;
        positions.push(x + Math.cos(a) * rad, y, z + Math.sin(a) * rad);
      }
    }
    for (let r = 0; r < ring; r++) {
      const a = v + r;
      const b = v + ((r + 1) % ring);
      const c = v + ring + r;
      const d = v + ring + ((r + 1) % ring);
      indices.push(a, c, b, b, c, d);
    }
    v += ring * 2;
  }
  if (v === 0) return null;
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(positions, 3));
  g.setIndex(indices);
  g.computeVertexNormals();
  return g;
}

/**
 * THE EDGE GIRDER (owner playtest 2026-08-02: "the vertical wires in the bridge
 * don't connect to anything").
 *
 * The suspenders hang on the cable line at |x| = BRIDGE_CABLE_X ≈ 8.3 m, but
 * the deck fascia stops at |x| = RAIL_OUTER + 0.15 ≈ 4.75 m, so every hanger
 * bottom used to dangle 3.5 m out over open water. A real suspension bridge
 * answers that with a stiffening girder along the cable line, cantilevered off
 * the deck — so that is what is built here:
 *
 *   - a continuous longitudinal BOX BEAM on the cable line, top face at
 *     `topY` = the hanger bottom, so the hangers die INSIDE it. It runs exactly
 *     the cable's own chunk range (anchorage to anchorage), so it starts and
 *     ends where the cable does and never sticks out past the span;
 *   - transverse CANTILEVER OUTRIGGERS on the BRIDGE_JOINTS_PER_CHUNK rhythm
 *     (one every 12.5 m, the same beat as the deck expansion joints) reaching
 *     from under the deck fascia out to the girder, so the girder visibly hangs
 *     off the deck instead of floating beside it.
 *
 * Both are a pure function of `localChunkIndex` (the range helpers below are
 * exported for biomes.geometry.test.ts), so cycle 2+ is bit-identical.
 *
 * The beam is deep enough to reach from the hanger bottom DOWN past the road
 * plane to the outriggers (which have to tuck under the deck fascia, below
 * y = 0, or they would punch through the road surface and the parapet).
 */
export const BRIDGE_GIRDER = {
  /** Cable line — the girder is centred under the suspenders. */
  x: BRIDGE_CABLE_X,
  /** Top face: the hanger bottom, so a suspender ends inside the steel. */
  topY: 1.0,
  /** Bottom face: below the road plane, where the outriggers meet it. */
  botY: -0.25,
  /** Beam width (x). */
  width: 0.5,
  /** Outrigger cross-section: (z) thickness x (y) depth. */
  outriggerZ: 0.36,
  outriggerY: 0.4,
  /** Outrigger centre height — wholly under the deck surface. */
  outriggerY0: -0.3,
  /** Inner end: under the deck fascia. */
  innerX: RAIL_OUTER,
  /** Longitudinal segments per chunk — the girder has to bend with the earth. */
  segments: 16,
} as const;

/** Bottom y of a suspender — it lands inside the girder box. */
const BRIDGE_SUSPENDER_BOT = BRIDGE_GIRDER.topY;

/**
 * Chunk-local z range of the edge girder for chunk `local`, or null. Identical
 * to the cable's own range by construction: the girder is the cable's deck.
 */
export function bridgeGirderRange(local: number): [number, number] | null {
  return bridgeCableRange(local);
}

/**
 * Chunk-local z of every cantilever outrigger in chunk `local` — the deck
 * expansion-joint rhythm (one every CHUNK_LENGTH / BRIDGE_JOINTS_PER_CHUNK =
 * 12.5 m), clipped to the stretch that actually carries girder.
 */
export function bridgeOutriggerZs(local: number): number[] {
  const range = bridgeGirderRange(local);
  if (!range) return [];
  const [z0, z1] = range;
  const step = CHUNK_LENGTH / BRIDGE_JOINTS_PER_CHUNK;
  const out: number[] = [];
  for (let i = 0; i < BRIDGE_JOINTS_PER_CHUNK; i++) {
    const z = (i + 0.5) * step;
    if (z >= z0 && z <= z1) out.push(z);
  }
  return out;
}

/** Append an axis-aligned box to a position/index soup. */
function pushBox(
  positions: number[],
  indices: number[],
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
): void {
  const v = positions.length / 3;
  const hx = sx / 2;
  const hy = sy / 2;
  const hz = sz / 2;
  for (const dz of [-hz, hz]) {
    for (const dy of [-hy, hy]) {
      for (const dx of [-hx, hx]) positions.push(cx + dx, cy + dy, cz + dz);
    }
  }
  // Corner order per z slab: 0 = (-x,-y), 1 = (+x,-y), 2 = (-x,+y), 3 = (+x,+y);
  // the far slab repeats at +4.
  const quads = [
    [0, 1, 3, 2], // near z
    [5, 4, 6, 7], // far z
    [4, 0, 2, 6], // -x
    [1, 5, 7, 3], // +x
    [4, 5, 1, 0], // -y
    [2, 3, 7, 6], // +y
  ];
  for (const q of quads) {
    indices.push(v + q[0], v + q[1], v + q[2], v + q[0], v + q[2], v + q[3]);
  }
}

/**
 * Edge girder + cantilever outriggers for ONE chunk at a given local index.
 *
 * ~192 tris of beam (16 segments, so it follows the curved-earth bend instead
 * of chording across 50 m) plus 12 tris per outrigger: under 250 tris per side
 * per chunk, ~2 k visible across the streamed window.
 */
function buildGirderGeometry(sideSign: number, local: number): BufferGeometry | null {
  const range = bridgeGirderRange(local);
  if (!range) return null;
  const [z0, z1] = range;
  const G = BRIDGE_GIRDER;
  const positions: number[] = [];
  const indices: number[] = [];
  const cy = (G.topY + G.botY) / 2;
  const sy = G.topY - G.botY;
  const seg = (z1 - z0) / G.segments;
  for (let i = 0; i < G.segments; i++) {
    pushBox(positions, indices, sideSign * G.x, cy, z0 + (i + 0.5) * seg, G.width, sy, seg);
  }
  // Cantilevers: from under the deck fascia out to (and into) the girder.
  const outerX = G.x + G.width / 2;
  const spanX = outerX - G.innerX;
  for (const z of bridgeOutriggerZs(local)) {
    pushBox(
      positions,
      indices,
      sideSign * (G.innerX + spanX / 2),
      G.outriggerY0,
      z,
      spanX,
      G.outriggerY,
      G.outriggerZ,
    );
  }
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(positions, 3));
  g.setIndex(indices);
  g.computeVertexNormals();
  return g;
}

/**
 * The end ANCHORAGE: a chunky beveled abutment mass at the roadside that the
 * main cable dies into.
 *
 * Two stacked masses (a wide battered base + a narrower cap) with the cap's top
 * at BRIDGE_SPAN.anchorY, so the cable's last sample lands INSIDE the block.
 * Built in chunk-local space at z = 0 and positioned per chunk. Inner face at
 * |x| = 6.3 m — clear of MIN_PROP_X.
 */
const BRIDGE_ABUTMENT = {
  x: 9.1,
  baseW: 5.6,
  baseD: 9.0,
  capW: 4.0,
  capD: 6.0,
  /** Base sits on the water plane and rises to just under the cap. */
  baseTop: 1.4,
  capTop: BRIDGE_SPAN.anchorY,
} as const;

/**
 * Bridge content: red portal towers at 75/175/275 m, a catenary that ANCHORS
 * into an abutment at both ends of the span, tight suspenders, a deck fascia so
 * the road reads as a raised deck, navigation lights on the towers, and boat
 * silhouettes + sparkle bands on the water.
 *
 * Visible budget: ~1-2 towers (4.6k each) + 8 cable/suspender meshes (~2.5k) +
 * abutments/fascia/joint/boat quads. ≈ 25k tris.
 */
function buildBridgeContent(t: Templates): BiomeContent {
  const towerTpl = t.bridgetower;
  const cableMat = plasticMaterial({ color: 0xc8402f, name: "bridgeCable", kind: "candy" });
  const deckMat = plasticMaterial({ color: 0x8d949c, name: "bridgeDeck" });
  const sparkleMat = plasticMaterial({ color: 0x8fe0f2, name: "waterSparkle" });
  sparkleMat.fog = true;
  const deckGeo = new BoxGeometry(0.5, BRIDGE_DECK_DROP, CHUNK_LENGTH, 1, 1, 10);
  // --- Deck detail ---------------------------------------------------------
  // Expansion joints: thin darker transverse lines every BRIDGE_JOINT_SPACING
  // metres. Unlit basic (like the lane markings) so they never pick up a coat
  // streak, and drawn 1.4 cm over the asphalt — under the 2 cm markings, so a
  // joint can never paint over a lane dash. They run 0.8 m past the corridor on
  // each side and disappear under the rail, which is what sells them as
  // structure rather than as paint.
  const jointGeo = new PlaneGeometry(CORRIDOR_WIDTH + 1.6, 0.16, 6, 1);
  jointGeo.rotateX(-Math.PI / 2);
  const jointMat = applyEarthCurve(new MeshBasicMaterial({ color: 0x7d848c }));
  jointMat.fog = true;
  // Low kerb along the deck edge, between the rail's outer face and the fascia.
  const kerbGeo = new BoxGeometry(0.3, 0.14, CHUNK_LENGTH, 1, 1, 10);
  const kerbMat = plasticMaterial({ color: shade(0x8d949c, 1.08), name: "bridgeKerb" });
  // Sparkle bands stretch up to 44 m along z, so they carry z-segments for the
  // bend - a single quad would sag 11 cm and sink under the water plane.
  const sparkleGeo = new PlaneGeometry(1, 1, 2, 8);
  sparkleGeo.rotateX(-Math.PI / 2);
  // --- Anchorage ----------------------------------------------------------
  const abutBaseGeo = new BoxGeometry(BRIDGE_ABUTMENT.baseW, 1, BRIDGE_ABUTMENT.baseD);
  const abutCapGeo = new BoxGeometry(BRIDGE_ABUTMENT.capW, 1, BRIDGE_ABUTMENT.capD);
  const abutMat = plasticMaterial({ color: 0x9aa0a8, name: "bridgeAbutment" });
  // --- Navigation lights (the DESIGN_SYSTEM brake-light-dot emissive rule) --
  const navGeo = new IcosahedronGeometry(0.42, 0);
  const navMat = new MeshBasicMaterial({ color: 0xff5d3c });
  navMat.fog = true;
  // --- Boats: two chunky masses (hull + cabin) far out on the water --------
  const boatGeo = new BoxGeometry(1, 1, 1);
  const boatMat = plasticMaterial({ color: 0xffffff, name: "bridgeBoat" });
  // One cable + suspender variant per (side, chunk-local index) — the span
  // layout is a pure function of the local index, so this table IS the parity
  // guarantee across a cycle wrap.
  const cableGeos = new Map<string, BufferGeometry>();
  const suspGeos = new Map<string, BufferGeometry>();
  const girderGeos = new Map<string, BufferGeometry>();
  for (const sideSign of [-1, 1] as const) {
    for (let local = 0; local < CHUNKS_PER_BIOME; local++) {
      const c = buildCableGeometry(sideSign, local);
      if (c) cableGeos.set(`${sideSign}_${local}`, c);
      const s = buildSuspenderGeometry(sideSign, local);
      if (s) suspGeos.set(`${sideSign}_${local}`, s);
      const gd = buildGirderGeometry(sideSign, local);
      if (gd) girderGeos.set(`${sideSign}_${local}`, gd);
    }
  }
  const owned: (BufferGeometry | Material)[] = [
    cableMat,
    deckMat,
    sparkleMat,
    deckGeo,
    sparkleGeo,
    jointGeo,
    jointMat,
    kerbGeo,
    kerbMat,
    abutBaseGeo,
    abutCapGeo,
    abutMat,
    navGeo,
    navMat,
    boatGeo,
    boatMat,
    ...cableGeos.values(),
    ...suspGeos.values(),
    ...girderGeos.values(),
  ];
  const fieldSet = new FieldSet();

  const factory: ChunkFactory = (group, F) => {
    const towers = towerTpl ? F.glb(towerTpl, 1) : null;
    // Sparkle bands lie FLAT on the water: a caster coplanar with its receiver
    // is nothing but acne, so they are excluded from the shadow pass.
    const sparkle = F.field(sparkleGeo, sparkleMat, 8, false, null, false);
    // Boats: distant silhouette masses, never casters (they are 60-190 m out).
    const boats = F.field(boatGeo, boatMat, 6, true, null, false);
    // Navigation dots: 4 per tower (two heights per leg line).
    const navs = F.field(navGeo, navMat, 6, false, null, false);

    // Deck fascia + kerb: static, chunk-local. The fascia casts onto the
    // water below the deck.
    for (const sideSign of [-1, 1]) {
      const fascia = new Mesh(deckGeo, deckMat);
      fascia.position.set(sideSign * (RAIL_OUTER + 0.15), -BRIDGE_DECK_DROP / 2, CHUNK_LENGTH / 2);
      fascia.frustumCulled = false;
      fascia.castShadow = true;
      fascia.receiveShadow = true;
      group.add(fascia);
      const kerb = new Mesh(kerbGeo, kerbMat);
      kerb.position.set(sideSign * (RAIL_OUTER - 0.05), 0.07, CHUNK_LENGTH / 2);
      kerb.frustumCulled = false;
      kerb.receiveShadow = true;
      group.add(kerb);
    }

    // Expansion joints: a fixed rhythm, so they line up across chunk seams.
    for (let i = 0; i < BRIDGE_JOINTS_PER_CHUNK; i++) {
      const joint = new Mesh(jointGeo, jointMat);
      joint.position.set(0, 0.014, (i + 0.5) * (CHUNK_LENGTH / BRIDGE_JOINTS_PER_CHUNK));
      joint.frustumCulled = false;
      group.add(joint);
    }

    // Anchorage masses — one pair per side, shown only on the two end chunks.
    const abutments: Mesh[] = [];
    const waterY = BIOMES.bridge_suspension.groundY;
    for (const sideSign of [-1, 1]) {
      const base = new Mesh(abutBaseGeo, abutMat);
      base.scale.y = BRIDGE_ABUTMENT.baseTop - waterY;
      base.position.set(sideSign * BRIDGE_ABUTMENT.x, (BRIDGE_ABUTMENT.baseTop + waterY) / 2, 0);
      const cap = new Mesh(abutCapGeo, abutMat);
      cap.scale.y = BRIDGE_ABUTMENT.capTop - BRIDGE_ABUTMENT.baseTop + 0.2;
      cap.position.set(
        sideSign * BRIDGE_ABUTMENT.x,
        (BRIDGE_ABUTMENT.capTop + BRIDGE_ABUTMENT.baseTop - 0.2) / 2,
        0,
      );
      for (const m of [base, cap]) {
        m.frustumCulled = false;
        m.castShadow = true;
        m.receiveShadow = true;
        group.add(m);
        abutments.push(m);
      }
    }

    // Every (side, local) cable/suspender variant exists as its own mesh; the
    // local index picks exactly one pair per side per populate.
    const cables: { mesh: Mesh; local: number }[] = [];
    for (const sideSign of [-1, 1] as const) {
      for (let local = 0; local < CHUNKS_PER_BIOME; local++) {
        const cg = cableGeos.get(`${sideSign}_${local}`);
        if (cg) {
          const cable = new Mesh(cg, cableMat);
          cable.frustumCulled = false;
          group.add(cable);
          cables.push({ mesh: cable, local });
        }
        const sg = suspGeos.get(`${sideSign}_${local}`);
        if (sg) {
          const susp = new Mesh(sg, cableMat);
          susp.frustumCulled = false;
          group.add(susp);
          cables.push({ mesh: susp, local });
        }
        // Edge girder + outriggers: the deck edge the hangers land on. It is
        // over open water, far outside the shadow frustum, so it neither
        // casts nor receives.
        const gg = girderGeos.get(`${sideSign}_${local}`);
        if (gg) {
          const girder = new Mesh(gg, cableMat);
          girder.frustumCulled = false;
          girder.castShadow = false;
          girder.receiveShadow = false;
          group.add(girder);
          cables.push({ mesh: girder, local });
        }
      }
    }

    return (slice: number): void => {
      const local = localChunkIndex(slice);
      for (const c of cables) c.mesh.visible = c.local === local;

      // --- Towers --------------------------------------------------------
      towers?.reset();
      const towerIdx = BRIDGE_TOWER_CHUNKS.indexOf(local);
      if (towers && towerIdx >= 0) {
        const zLocal = (local + 1) * CHUNK_LENGTH - BRIDGE_SPAN.towers[towerIdx];
        towers.add(0, 0, zLocal, 0, BRIDGE_TOWER_SCALE);
      }
      towers?.flush();

      // --- Navigation lights on the tower tops ---------------------------
      navs.reset();
      if (towerIdx >= 0) {
        const zLocal = (local + 1) * CHUNK_LENGTH - BRIDGE_SPAN.towers[towerIdx];
        for (const sideSign of [-1, 1]) {
          navs.add(sideSign * BRIDGE_CABLE_X, BRIDGE_TOWER_TOP - 0.6, zLocal, 0, 1);
          navs.add(sideSign * BRIDGE_CABLE_X, BRIDGE_TOWER_TOP * 0.62, zLocal, 0, 0.72);
        }
      }
      navs.flush();

      // --- Anchorage ------------------------------------------------------
      const anchorSlot = BRIDGE_ANCHOR_CHUNKS.indexOf(local);
      const showAbut = anchorSlot >= 0;
      if (showAbut) {
        const bz = anchorSlot === 0 ? BRIDGE_SPAN.anchorStart : BRIDGE_SPAN.anchorEnd;
        const zLocal = (local + 1) * CHUNK_LENGTH - bz;
        for (const m of abutments) m.position.z = zLocal;
      }
      for (const m of abutments) m.visible = showAbut;

      // --- Water dressing --------------------------------------------------
      sparkle.reset();
      for (let i = 0; i < 8; i++) {
        const sideSign = i % 2 === 0 ? -1 : 1;
        const x = sideSign * rndRange(slice, 300 + i, 14, 130);
        const len = rndRange(slice, 320 + i, 16, 44);
        const w = rndRange(slice, 340 + i, 3, 8);
        // On the WATER, not the deck: the chunk group sits at y = 0 (road
        // level) and the water plane is the global ground at the biome's
        // groundY, so the bands have to be written down there.
        sparkle.add(
          x,
          BIOMES.bridge_suspension.groundY + 0.06,
          rndRange(slice, 360 + i, 0, CHUNK_LENGTH),
          0,
          w,
          1,
          len,
        );
      }
      sparkle.flush();

      // Boats: a hull mass plus a cabin mass, deterministic, far out enough
      // that they only ever read as silhouettes against the water.
      boats.reset();
      for (let i = 0; i < 2; i++) {
        if (rnd(slice, 700 + i) < 0.45) continue;
        const sideSign = rnd(slice, 710 + i) > 0.5 ? 1 : -1;
        const x = sideSign * rndRange(slice, 720 + i, 55, 190);
        const z = rndRange(slice, 730 + i, 0, CHUNK_LENGTH);
        const len = rndRange(slice, 740 + i, 11, 26);
        const beam = len * 0.28;
        const y = BIOMES.bridge_suspension.groundY;
        const rot = rndRange(slice, 750 + i, -0.5, 0.5);
        boats.add(x, y + 0.7, z, rot, beam, 1.4, len, 0x6f8496);
        boats.add(x, y + 2.0, z + Math.cos(rot) * len * 0.12, rot, beam * 0.62, 1.5, len * 0.3, 0xd8e2ea);
      }
      boats.flush();
    };
  };

  return {
    chunks: buildChunks(factory, fieldSet),
    fields: fieldSet,
    backdrop: bridgeBackdrop(),
    dispose(): void {
      for (const o of owned) o.dispose();
      fieldSet.dispose();
    },
  };
}

/**
 * Parallel bridges + far headland (owner playtest 2026-08-02: "the distant
 * horizontal bridge background is weird, especially the distant grey blob").
 *
 * WHAT WAS WRONG. The silhouette cards are authored in the XY plane and parked
 * BACKDROP_DIST (250 m) ahead, and they take the curved-earth bend like all
 * world scenery — which drops a point at horizontal distance d by d²/2R (14 m
 * at 250 m). The old deck lines were authored at y = 20 / 26, which after the
 * bend still left them ~19 m ABOVE the water's visible horizon: a hard
 * horizontal bar hanging in clear sky with its tower legs dangling under it,
 * and high enough to be past the strong part of the horizon haze band, so
 * nothing softened its edges. Worse, each deck ran from base − 70 to base + 70,
 * which for the base = ±80 layer reached |x| = 10 m — the road's own vanishing
 * point — so a pale slab sat wedged between our tower legs: the "grey blob".
 * The two near layers (±80 and ±150, 140 m wide each) also abutted EXACTLY,
 * fusing into one unbroken 210 m bar per side.
 *
 * WHAT IT DOES NOW.
 * 1. Every deck is authored AT its own local horizon: `horizonY(x)` inverts the
 *    bend, so a deck line lands a metre or two over the water line wherever it
 *    sits in the frame, and its legs sink below it (the opaque water plane
 *    paints over the card, which is what "feet below the horizon" looks like).
 * 2. Decks start at |x| ≥ 30 m — outside the corridor's vanishing point — and
 *    the two layers per side are separated in x AND in depth, so the estuary
 *    reads as several crossings instead of one bar.
 * 3. The palette is the haze palette: these tones are a short step off the
 *    biome fog (0xaad6ec) instead of the old grey-blues, so a silhouette edge
 *    is no harder than the rest of the horizon once the haze band is over it.
 */
function bridgeBackdrop(): Backdrop {
  const far: [number, number, number, number][] = [];
  const near: [number, number, number, number][] = [];
  /**
   * World y at which a point of the card at lateral `x` lands exactly on the
   * bent world's visible horizon: the bend pushes it down by earthDrop(d) and
   * the horizon itself sits horizonAngle() below the eye at that distance.
   */
  const horizonY = (x: number): number => {
    const d = Math.hypot(x, BACKDROP_DIST);
    return EYE_HEIGHT + earthDrop(d) - d * Math.tan(horizonAngle());
  };
  // Parallel decks left and right — two depth layers per side, so the water
  // reads as a busy estuary rather than as one lonely crossing.
  const deck = (
    into: [number, number, number, number][],
    base: number,
    rise: number,
    k: number,
  ): void => {
    const y = horizonY(base) + rise;
    const w = 100 * k;
    const legDx = 21 * k;
    const legH = 23 * k;
    into.push([base - w / 2, y, w, 2.4 * k]); // deck
    into.push([base - legDx, y, 4 * k, legH]); // tower leg
    into.push([base + legDx - 4 * k, y, 4 * k, legH]); // tower leg
    into.push([base - legDx, y + legH - 2.2 * k, 2 * legDx, 2.2 * k]); // crossbeam
    for (let i = 1; i < 8; i++) {
      into.push([base - w / 2 + i * (w / 8), y + 2.4 * k, 1 * k, 13 * k]); // hanger
    }
  };
  for (const dir of [-1, 1]) {
    // Near crossing: inner end at |x| = 30 m, clear of the road corridor. The
    // rise is deliberately small — a few metres over the water line is all a
    // deck ever shows at 250 m, and it is what keeps the card inside the
    // strong part of the haze band instead of standing over it.
    deck(near, dir * 80, 3.5, 1);
    // Far crossing: a separate span further out and further back, with a gap
    // between the two so they never fuse into a single horizontal bar.
    deck(far, dir * 178, 4.5, 0.78);
  }
  const hills: number[][] = [];
  for (let i = 0; i < 12; i++) {
    const x = -420 + i * 76;
    // Headland feet sit UNDER the water line by construction, so only the
    // humps clear the horizon — the same read as the real far shore.
    const base = horizonY(x + 40) - 6;
    hills.push([x, base, x + 40, base + 14 + hashInt(i * 31 + 5) * 16, x + 82, base]);
  }
  return makeBackdrop([
    // Haze palette: each tone is a short, cool step off the biome fog
    // (0xaad6ec). The horizon band washes them to ~20% of their own contrast,
    // so this is what a legible-but-atmospheric silhouette costs.
    { geo: trisGeometry(hills), color: 0x9dc0d8 },
    { geo: quadsGeometry(far), color: 0x93b8d2, z: 3 },
    { geo: quadsGeometry(near), color: 0x7fa8c8, z: 6 },
  ]);
}

// ---------------------------------------------------------------------------
// Forest biome
// ---------------------------------------------------------------------------

const CONIFER_GREENS = [0x2f6b3c, 0x3f8a4e, 0x245231];

/**
 * Forest floor: three grass tones (±10% of the biome ground 0x3e6b3a plus the
 * palette's mid conifer green) blotched over the ground plane, with rock and
 * bush blobs on top. Per chunk: 22 patches/side (6 tris) + 12 blobs/side (20
 * tris) = ~744 triangles.
 */
const FOREST_GROUND = {
  patches: 22,
  blobs: 12,
  /** Patch y — a few cm over the ground plane at -0.06, never coplanar. */
  patchY: -0.03,
  tones: [shade(0x3e6b3a, 1.1), shade(0x3e6b3a, 0.92), 0x3f8a4e],
  /** Rock brown-grey + two bush greens, all DESIGN_SYSTEM palette entries. */
  blobTones: [0x6b4a36, 0x245231, 0x2f6b3c],
} as const;

/**
 * Forest content: the GLB pines hold the near roadside band (3 per side per
 * chunk — 24 visible × ~3.3k ≈ 79k tris) and a cloud of ~16-triangle procedural
 * cones fills the depth behind them, which is what actually reads as "dense".
 */
function buildForestContent(t: Templates): BiomeContent {
  const pineKeys: EnvKey[] = (["pineA", "pineB"] as EnvKey[]).filter((k) => t[k]);
  const coneGeo = new ConeGeometry(1, 1, 7, 1);
  coneGeo.translate(0, 0.5, 0);
  const coneMat = plasticMaterial({ color: 0xffffff, name: "forestFiller" });
  const trunkGeo = new CylinderGeometry(0.16, 0.22, 1, 5);
  trunkGeo.translate(0, 0.5, 0);
  const trunkMat = plasticMaterial({ color: 0x6b4a36, name: "forestTrunk" });
  // --- Ground: layered grass-tone patches + scattered rock/bush blobs ------
  // The patches are 6-gon quads (6 tris) laid a few cm over the ground plane in
  // three greens; the blobs are subdivision-0 icosahedra (20 tris) squashed in
  // y, which is exactly the "hard low-poly facets" the geometry rules allow.
  // RingGeometry(0, 1, ...) is a disc WITH radial rings: an 11 m-radius patch
  // spans 22 m and a single-ring fan would chord under the bent ground plane.
  const patchGeo = new RingGeometry(0, 1, 6, 2);
  patchGeo.rotateX(-Math.PI / 2);
  const patchMat = plasticMaterial({ color: 0xffffff, name: "forestPatch" });
  const blobGeo = new IcosahedronGeometry(1, 0);
  const blobMat = plasticMaterial({ color: 0xffffff, name: "forestBlob" });
  const owned: (BufferGeometry | Material)[] = [
    coneGeo,
    coneMat,
    trunkGeo,
    trunkMat,
    patchGeo,
    patchMat,
    blobGeo,
    blobMat,
  ];
  const fieldSet = new FieldSet();

  const factory: ChunkFactory = (_group, F) => {
    const pines = pineKeys.map((k) => F.glb(t[k]!, 4));
    const cones = F.field(coneGeo, coneMat, 36, true);
    const trunks = F.field(trunkGeo, trunkMat, 36);
    const patches = F.field(patchGeo, patchMat, FOREST_GROUND.patches * 2, true);
    const blobs = F.field(blobGeo, blobMat, FOREST_GROUND.blobs * 2, true);

    return (slice: number): void => {
      for (const p of pines) p.reset();
      cones.reset();
      trunks.reset();
      patches.reset();
      blobs.reset();
      for (const sideSign of [-1, 1]) {
        const s = sideSign > 0 ? 0 : 500;

        // --- Ground: grass tone patches, then rock/bush blobs --------------
        for (let i = 0; i < FOREST_GROUND.patches; i++) {
          const rad = rndRange(slice, s + 700 + i, 3.5, 11);
          patches.add(
            sideSign * (GROUND_DETAIL_MIN_X + rnd(slice, s + 730 + i) ** 1.5 * 55),
            FOREST_GROUND.patchY,
            rndRange(slice, s + 760 + i, -4, CHUNK_LENGTH + 4),
            rndRange(slice, s + 790 + i, 0, Math.PI),
            rad,
            1,
            rad * rndRange(slice, s + 820 + i, 0.65, 1.35),
            FOREST_GROUND.tones[
              Math.floor(rnd(slice, s + 850 + i) * FOREST_GROUND.tones.length)
            ],
          );
        }
        for (let i = 0; i < FOREST_GROUND.blobs; i++) {
          const rad = rndRange(slice, s + 880 + i, 0.45, 1.5);
          blobs.add(
            sideSign * (GROUND_DETAIL_MIN_X + 0.5 + rnd(slice, s + 900 + i) ** 1.6 * 34),
            rad * 0.28,
            rndRange(slice, s + 920 + i, 0, CHUNK_LENGTH),
            rndRange(slice, s + 940 + i, 0, Math.PI * 2),
            rad,
            rad * rndRange(slice, s + 960 + i, 0.45, 0.8),
            rad * rndRange(slice, s + 980 + i, 0.8, 1.25),
            FOREST_GROUND.blobTones[
              Math.floor(rnd(slice, s + 995 + i) * FOREST_GROUND.blobTones.length)
            ],
          );
        }

        // Near band: hero GLB pines, alternating archetypes.
        // 3 per side per chunk: at 4 the GLB pines alone measured 174k tris
        // in the visible window, which is most of the whole-scene budget.
        for (let i = 0; i < 3; i++) {
          if (pines.length === 0) break;
          const field = pines[(i + (sideSign > 0 ? 0 : 1)) % pines.length];
          const depth = rnd(slice, s + 60 + i) ** 2 * 6;
          field.add(
            sideSign * (MIN_PROP_X + 0.3 + depth),
            0,
            rndRange(slice, s + 70 + i, 4, CHUNK_LENGTH - 4),
            rndRange(slice, s + 80 + i, 0, Math.PI * 2),
            // 7 m pines read small from a 1.35 m eye height; the arcade
            // exaggeration in DESIGN_SYSTEM's geometry rules buys the scale up.
            rndRange(slice, s + 90 + i, 1.1, 1.9),
          );
        }
        // Depth band: cheap cones with trunks.
        for (let i = 0; i < 18; i++) {
          const depth = 8 + rnd(slice, s + 100 + i) ** 1.4 * 70;
          const x = sideSign * (MIN_PROP_X + depth);
          const z = rndRange(slice, s + 130 + i, -2, CHUNK_LENGTH + 2);
          const h = rndRange(slice, s + 160 + i, 9, 19);
          const rad = h * rndRange(slice, s + 190 + i, 0.18, 0.28);
          const color = CONIFER_GREENS[Math.floor(rnd(slice, s + 220 + i) * CONIFER_GREENS.length)];
          cones.add(x, h * 0.18, z, 0, rad, h * 0.9, rad, color);
          trunks.add(x, 0, z, 0, 1, h * 0.2, 1);
        }
      }
      for (const p of pines) p.flush();
      cones.flush();
      trunks.flush();
      patches.flush();
      blobs.flush();
    };
  };

  return {
    chunks: buildChunks(factory, fieldSet),
    fields: fieldSet,
    backdrop: forestBackdrop(),
    dispose(): void {
      for (const o of owned) o.dispose();
      fieldSet.dispose();
    },
  };
}

/** Two ridge layers with snow caps. */
function forestBackdrop(): Backdrop {
  const far: number[][] = [];
  const near: number[][] = [];
  const caps: number[][] = [];
  for (let i = 0; i < 14; i++) {
    const x = -460 + i * 68;
    const h = 74 + hashInt(i * 47 + 3) * 62;
    far.push([x, 0, x + 42, h, x + 90, 0]);
    // Snow cap: the top ~22% of the same triangle.
    const capH = h * 0.22;
    const capHalf = (42 * capH) / h;
    caps.push([x + 42 - capHalf, h - capH, x + 42, h, x + 42 + capHalf * 1.15, h - capH]);
  }
  for (let i = 0; i < 18; i++) {
    const x = -450 + i * 54;
    near.push([x, 0, x + 34, 34 + hashInt(i * 53 + 7) * 30, x + 72, 0]);
  }
  return makeBackdrop([
    { geo: trisGeometry(far), color: 0xc7d4e2 },
    { geo: trisGeometry(caps), color: 0xffffff, z: 1 },
    { geo: trisGeometry(near), color: 0x9fbcbe, z: 6 },
  ]);
}

// ---------------------------------------------------------------------------
// Fields biome
// ---------------------------------------------------------------------------

/**
 * Wheat-field ground dressing. Per chunk: 6 stripe quads + up to 3 mown circles
 * (14 tris) + 2 × 8 fence posts (12 tris) ≈ 250 triangles.
 */
const FIELDS_GROUND = {
  stripes: 3,
  mown: 3,
  postsPerSide: 8,
  /**
   * Fence x is now CONSTANT per side (only the post height is jittered), and
   * two horizontal rails run the full chunk between the posts. That is what
   * makes the fence continuous across a seam instead of a stuttering dotted
   * line — the 2026-08-01 owner note "fence continuity".
   */
  fenceX: GROUND_DETAIL_MIN_X + 0.25,
  railY: [0.55, 0.95] as const,
  /** Palette wheat pair plus a bleached +10% variant of the ground colour. */
  tones: [0xd9a93f, 0xe8c25a, shade(0xe8c25a, 1.09)],
  /** Hay bales per chunk (was 3 — the fields read as empty at that count). */
  bales: 4,
  /** Cheap faceted bush/crop-clump blobs, per side per chunk. */
  clumps: 7,
  clumpTones: [0xd9a93f, 0x9a7c38, 0x6b8f45],
} as const;

/**
 * Wheat fields: two-tone ground striping (large flat quads just above the
 * ground plane), sparse barns/windmills, scattered hay bales.
 *
 * NOTE: windmill.glb is a single fused mesh (no named blade node — see
 * ASSET_PIPELINE), so the "slow blade spin" option in the brief is not
 * available; the sails are static.
 */
function buildFieldsContent(t: Templates): BiomeContent {
  // 8x8: a stripe scales out to 160 m across, where an unsegmented quad would
  // chord 1.45 m under the bent ground and disappear (earthCurve.ts).
  const stripeGeo = new PlaneGeometry(1, 1, 8, 8);
  stripeGeo.rotateX(-Math.PI / 2);
  // White base + per-instance tint: the stripes now carry three wheat tones
  // (palette #e8c25a / #d9a93f plus a bleached ±10% variant) instead of one
  // flat darker band, which is what makes the striping read as a crop pattern
  // rather than as a grey card lying on the field.
  const stripeMat = plasticMaterial({ color: 0xffffff, name: "wheatStripe" });
  // Mown circles: a pivot-irrigation motif, 14-gon quads over the ground plane.
  const mownGeo = new RingGeometry(0, 1, 14, 3);
  mownGeo.rotateX(-Math.PI / 2);
  const mownMat = plasticMaterial({ color: shade(0xe8c25a, 1.1), name: "wheatMown" });
  // Fence posts along the shoulder — chunky boxes, palette trunk brown.
  const postGeo = new BoxGeometry(0.11, 1, 0.11);
  postGeo.translate(0, 0.5, 0);
  const postMat = plasticMaterial({ color: 0x6b4a36, name: "fieldFencePost" });
  // Continuous fence rails: two long boxes per side, static per chunk, so the
  // line never breaks at a seam.
  const railGeo = new BoxGeometry(0.07, 0.1, CHUNK_LENGTH);
  const railMat = plasticMaterial({ color: shade(0x6b4a36, 1.12), name: "fieldFenceRail" });
  // Crop clumps: faceted blobs (the forest's ground-cover recipe in wheat
  // tones) — the cheap layer that stops the field reading as a flat card.
  const clumpGeo = new IcosahedronGeometry(1, 0);
  const clumpMat = plasticMaterial({ color: 0xffffff, name: "fieldClump" });
  const owned: (BufferGeometry | Material)[] = [
    stripeGeo,
    stripeMat,
    mownGeo,
    mownMat,
    postGeo,
    postMat,
    railGeo,
    railMat,
    clumpGeo,
    clumpMat,
  ];
  const fieldSet = new FieldSet();

  const factory: ChunkFactory = (group, F) => {
    // Field striping + mown circles are flat decals on the ground plane —
    // never casters. Fence posts are real 3D and cast.
    const stripes = F.field(stripeGeo, stripeMat, FIELDS_GROUND.stripes * 2, true, null, false);
    const mown = F.field(mownGeo, mownMat, FIELDS_GROUND.mown, false, null, false);
    const posts = F.field(postGeo, postMat, FIELDS_GROUND.postsPerSide * 2);
    const clumps = F.field(clumpGeo, clumpMat, FIELDS_GROUND.clumps * 2, true);
    for (const sideSign of [-1, 1]) {
      for (const y of FIELDS_GROUND.railY) {
        const rail = new Mesh(railGeo, railMat);
        rail.position.set(sideSign * FIELDS_GROUND.fenceX, y, CHUNK_LENGTH / 2);
        rail.frustumCulled = false;
        rail.castShadow = true;
        rail.receiveShadow = true;
        group.add(rail);
      }
    }
    // 3 near bales + 3 mid-field ones; 2 hero slots (near + a distant
    // mid-rank instance at a bigger scale) for the barn and the windmill.
    const bales = t.haybale ? F.glb(t.haybale, FIELDS_GROUND.bales) : null;
    const barns = t.barn ? F.glb(t.barn, 2) : null;
    const mills = t.windmill ? F.glb(t.windmill, 2) : null;
    const trees = t.autumnTree ? F.glb(t.autumnTree, 2, true) : null;

    return (slice: number): void => {
      stripes.reset();
      mown.reset();
      posts.reset();
      clumps.reset();
      bales?.reset();
      barns?.reset();
      mills?.reset();
      trees?.reset();

      // Crop striping: three bands per side now, with JITTERED width (the old
      // pass drew two bands of near-identical length, which read as a repeating
      // ladder) and a tone drawn from the wheat palette.
      for (const sideSign of [-1, 1]) {
        const s = sideSign > 0 ? 0 : 500;
        for (let i = 0; i < FIELDS_GROUND.stripes; i++) {
          const len = rndRange(slice, s + 400 + i, 6, 30);
          const inner = rndRange(slice, s + 410 + i, GROUND_DETAIL_MIN_X, 40);
          const outer = inner + rndRange(slice, s + 420 + i, 50, 160);
          stripes.add(
            (sideSign * (inner + outer)) / 2,
            0.02,
            rndRange(slice, s + 430 + i, 4, CHUNK_LENGTH - 4),
            0,
            outer - inner,
            1,
            len,
            FIELDS_GROUND.tones[
              Math.floor(rnd(slice, s + 450 + i) * FIELDS_GROUND.tones.length)
            ],
          );
        }

        // Fence posts: an evenly spaced run along the shoulder at a CONSTANT
        // x (the rails behind them are one long box per chunk), height
        // jittered only — see FIELDS_GROUND.fenceX.
        for (let i = 0; i < FIELDS_GROUND.postsPerSide; i++) {
          posts.add(
            sideSign * FIELDS_GROUND.fenceX,
            0,
            (i + 0.5) * (CHUNK_LENGTH / FIELDS_GROUND.postsPerSide),
            rndRange(slice, s + 540 + i, -0.12, 0.12),
            1,
            rndRange(slice, s + 560 + i, 1.05, 1.2),
            1,
          );
        }

        // Crop clumps: faceted blobs out in the field, the fields' answer to
        // the forest's rock/bush ground cover.
        for (let i = 0; i < FIELDS_GROUND.clumps; i++) {
          const rad = rndRange(slice, s + 1000 + i, 0.6, 2.1);
          clumps.add(
            sideSign * (GROUND_DETAIL_MIN_X + 2 + rnd(slice, s + 1030 + i) ** 1.5 * 60),
            rad * 0.3,
            rndRange(slice, s + 1060 + i, -3, CHUNK_LENGTH + 3),
            rndRange(slice, s + 1090 + i, 0, Math.PI * 2),
            rad,
            rad * rndRange(slice, s + 1120 + i, 0.45, 0.85),
            rad * rndRange(slice, s + 1150 + i, 0.8, 1.3),
            FIELDS_GROUND.clumpTones[
              Math.floor(rnd(slice, s + 1180 + i) * FIELDS_GROUND.clumpTones.length)
            ],
          );
        }
      }

      // Mown circles — sparse, so they land as an occasional landmark.
      for (let i = 0; i < FIELDS_GROUND.mown; i++) {
        if (rnd(slice, 580 + i) < 0.45) continue;
        const sideSign = rnd(slice, 585 + i) > 0.5 ? 1 : -1;
        const rad = rndRange(slice, 590 + i, 9, 22);
        mown.add(
          sideSign * (25 + rnd(slice, 595 + i) * 70),
          0.015,
          rndRange(slice, 598 + i, 0, CHUNK_LENGTH),
          0,
          rad,
          1,
          rad,
        );
      }

      // NEAR rank: one barn OR windmill per chunk, ~70% of chunks.
      const heroSide = rnd(slice, 440) > 0.5 ? 1 : -1;
      const roll = rnd(slice, 441);
      if (roll < 0.5 && barns) {
        barns.add(
          heroSide * rndRange(slice, 442, 12, 22),
          0,
          rndRange(slice, 443, 10, CHUNK_LENGTH - 10),
          rndRange(slice, 444, -0.6, 0.6) + (heroSide > 0 ? Math.PI : 0),
          rndRange(slice, 445, 1.25, 1.6),
        );
      } else if (roll < 0.9 && mills) {
        mills.add(
          heroSide * rndRange(slice, 446, 14, 28),
          0,
          rndRange(slice, 447, 8, CHUNK_LENGTH - 8),
          rndRange(slice, 448, -0.5, 0.5) + (heroSide > 0 ? Math.PI : 0),
          rndRange(slice, 449, 1.2, 1.6),
        );
      }

      // MID rank: a distant farmstead on the far side, 45-105 m out, at a
      // bigger scale so it still reads at that range. This is the layer the
      // forest gets from its cone band and the fields had nothing of.
      const midSide = rnd(slice, 640) > 0.5 ? 1 : -1;
      const midRoll = rnd(slice, 641);
      if (midRoll < 0.4 && barns) {
        barns.add(
          midSide * rndRange(slice, 642, 45, 105),
          0,
          rndRange(slice, 643, 6, CHUNK_LENGTH - 6),
          rndRange(slice, 644, -1.2, 1.2),
          rndRange(slice, 645, 1.9, 3.0),
        );
      } else if (midRoll < 0.78 && mills) {
        mills.add(
          midSide * rndRange(slice, 646, 45, 105),
          0,
          rndRange(slice, 647, 6, CHUNK_LENGTH - 6),
          rndRange(slice, 648, -1.2, 1.2),
          rndRange(slice, 649, 1.8, 2.8),
        );
      }

      // Tree clusters: autumn_tree re-instanced and TINTED wheat-gold/green,
      // so the fields get a vertical mid rank without a new asset.
      if (trees) {
        for (let i = 0; i < 2; i++) {
          if (rnd(slice, 660 + i) < 0.62) continue;
          const sideSign = rnd(slice, 670 + i) > 0.5 ? 1 : -1;
          trees.add(
            sideSign * rndRange(slice, 680 + i, 16, 70),
            0,
            rndRange(slice, 690 + i, 4, CHUNK_LENGTH - 4),
            rndRange(slice, 700 + i, 0, Math.PI * 2),
            rndRange(slice, 710 + i, 1.1, 2.0),
            undefined,
            undefined,
            instanceTint(
              rndRange(slice, 720 + i, 0.85, 1.05),
              rndRange(slice, 730 + i, -0.08, 0.02),
            ),
          );
        }
      }

      // Hay bales scattered near the roadside and out in the field.
      if (bales) {
        for (let i = 0; i < FIELDS_GROUND.bales; i++) {
          if (rnd(slice, 460 + i) < 0.08) continue;
          const sideSign = rnd(slice, 470 + i) > 0.5 ? 1 : -1;
          const far = i >= 2;
          bales.add(
            sideSign *
              (far
                ? rndRange(slice, 480 + i, 18, 55)
                : rndRange(slice, 480 + i, MIN_PROP_X + 1.5, 15)),
            0,
            rndRange(slice, 490 + i, 3, CHUNK_LENGTH - 3),
            rndRange(slice, 500 + i, 0, Math.PI * 2),
            rndRange(slice, 510 + i, 1.1, far ? 2.1 : 1.7),
          );
        }
      }

      stripes.flush();
      mown.flush();
      posts.flush();
      clumps.flush();
      bales?.flush();
      barns?.flush();
      mills?.flush();
      trees?.flush();
    };
  };

  return {
    chunks: buildChunks(factory, fieldSet),
    fields: fieldSet,
    backdrop: fieldsBackdrop(),
    dispose(): void {
      for (const o of owned) o.dispose();
      fieldSet.dispose();
    },
  };
}

/** Low golden hills under a big low sun. */
function fieldsBackdrop(): Backdrop {
  const hills: number[][] = [];
  for (let i = 0; i < 16; i++) {
    const x = -460 + i * 60;
    hills.push([x, 0, x + 34, 8 + hashInt(i * 61 + 13) * 12, x + 70, 0]);
  }
  const sunGeo = new CircleGeometry(42, 32);
  return makeBackdrop([
    { geo: sunGeo, color: 0xffd98f, y: 20, z: -8 },
    { geo: trisGeometry(hills), color: 0xd9a93f, z: 6 },
  ]);
}

// ---------------------------------------------------------------------------
// Tunnel biome
// ---------------------------------------------------------------------------

const TUNNEL = {
  /** Springline |x| of the wall — clear of MIN_PROP_X with room to spare. */
  wallX: 6.8,
  /** Wall height before the arch springs. */
  springY: 3.2,
  /** Crown height. */
  crownY: 9.6,
  zSegments: 4,
  /**
   * Ceiling STRIP LIGHTS: two rows, this many per chunk per row.
   *
   * 5 (was 4) so the pitch is exactly CHUNK_LENGTH / 5 = 10 m — the rhythm a
   * real road tunnel runs, and short enough that at the biome's fog far (72 m)
   * there are always ~7 fittings receding down the tube. Since the 2026-08-02
   * light profile the tunnel's key light IS these lamps, so the rhythm is the
   * lighting, not decoration.
   */
  stripsPerRow: 5,
  /** Lateral x of the two lamp rows, and their height under the crown. */
  lampX: 2.4,
  lampDrop: 0.55,
  /**
   * Wall dressing (playtest 2026-08-01). Vertical panel seams every
   * CHUNK_LENGTH / seamsPerSide metres pin a scale to the tube — without them
   * the walls are an unbroken concrete gradient and the tunnel reads as a fog
   * corridor rather than as a built structure. A maintenance walkway kerb runs
   * along the wall base for the same reason.
   */
  seamsPerSide: 10,
  seamW: 0.07,
  walkwayX: 6.3,
  walkwayW: 0.9,
  walkwayH: 0.22,
  /**
   * Wall ALCOVES + overhead SIGN GANTRIES (2026-08-01 density pass). The
   * tunnel stays deliberately sparse — it is the contrast beat between two
   * dense outdoor biomes — so this is the whole addition: two recessed niches
   * per side per chunk (a dark inset panel with a chunky service cabinet
   * standing in it) and a gantry silhouette across the crown on 2 chunks in 8.
   */
  alcovesPerSide: 2,
  alcoveW: 2.4,
  alcoveH: 2.4,
  gantryEveryChunks: 4,
} as const;

/**
 * Procedural tunnel tube for ONE chunk (shared geometry across chunks).
 *
 * Profile: vertical walls to the springline, then a half-ellipse crown. Vertex
 * colours run the DESIGN_SYSTEM concrete ramp #c7b6a4 (springline) -> #8a7460
 * (crown); the biome's dense fog (near 6, far 72) supplies the depth falloff
 * down the tube.
 */
function buildTunnelGeometry(): BufferGeometry {
  const profile: [number, number][] = [];
  const arcSteps = 10;
  profile.push([-TUNNEL.wallX, 0]);
  profile.push([-TUNNEL.wallX, TUNNEL.springY]);
  for (let i = 1; i < arcSteps; i++) {
    const a = Math.PI - (i / arcSteps) * Math.PI; // pi -> 0
    profile.push([
      Math.cos(a) * TUNNEL.wallX,
      TUNNEL.springY + Math.sin(a) * (TUNNEL.crownY - TUNNEL.springY),
    ]);
  }
  profile.push([TUNNEL.wallX, TUNNEL.springY]);
  profile.push([TUNNEL.wallX, 0]);

  const lo = new Color(0xc7b6a4);
  const hi = new Color(0x8a7460);
  const pos: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const cols = profile.length;
  // Longitudinal OVERLAP, same trick as the road ribbon (world.ts
  // SEG_OVERLAP): consecutive chunks are yawed a fraction of a degree apart on
  // a bend and would otherwise open a hairline gap at every join — which reads
  // as a dark crack ringing the tube every 50 m.
  const over = 0.35;
  for (let s = 0; s <= TUNNEL.zSegments; s++) {
    const z = -over + (s / TUNNEL.zSegments) * (CHUNK_LENGTH + 2 * over);
    for (const [x, y] of profile) {
      pos.push(x, y, z);
      const k = clamp01(y / TUNNEL.crownY);
      col.push(lerp(lo.r, hi.r, k), lerp(lo.g, hi.g, k), lerp(lo.b, hi.b, k));
    }
  }
  for (let s = 0; s < TUNNEL.zSegments; s++) {
    for (let c = 0; c < cols - 1; c++) {
      const a = s * cols + c;
      const b = a + 1;
      const d = a + cols;
      const e = d + 1;
      idx.push(a, d, b, b, d, e);
    }
  }
  const g = new BufferGeometry();
  g.setAttribute("position", new Float32BufferAttribute(pos, 3));
  g.setAttribute("color", new Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/**
 * Tunnel content: an enclosing arch on every chunk of the biome, sodium strip
 * light FITTINGS on the ceiling every 10 m (unlit MeshBasic tube + dark housing
 * + additive halo — the DESIGN_SYSTEM emissive allowance, no real point lights),
 * and additive mouth-glow quads on the first and last chunk.
 *
 * LIGHTING (2026-08-02, owner playtest: "change the lighting in the tunnel to
 * match a tunnel's lighting"). The rig is no longer left alone in here. The
 * tunnel carries a LIGHT PROFILE (`sunMul`/`hemiMul`/`envMul` in BIOMES.tunnel,
 * applied through World.setLightProfile from the same blend as fog and haze):
 * the sun and the gradient environment drop to ~12-15%, the sodium hemisphere
 * comes up 4.2x, and the fittings above are what the player sees emitting it.
 * The rider's shadow survives as a faint smudge rather than a noon-hard
 * silhouette, which is the point.
 */
function buildTunnelContent(): BiomeContent {
  const tubeGeo = buildTunnelGeometry();
  const tubeMat = plasticMaterial({ color: 0xffffff, name: "tunnelConcrete" });
  tubeMat.vertexColors = true;
  tubeMat.side = DoubleSide;
  // --- Ceiling light fixtures (2026-08-02 tunnel lighting pass) ------------
  //
  // The tunnel's light profile takes the sun and the environment almost all the
  // way out and pushes the sodium hemisphere up (BIOMES.tunnel), so the player
  // is now standing in lamp light with nothing on screen emitting it. These are
  // the emitters: a dark HOUSING, a hot emissive TUBE inside it, and a soft
  // additive HALO under it, three instanced fields on a 10 m pitch.
  //
  // NO REAL LIGHTS. Ten point lights per chunk would be ten more shadow-less
  // forward-lighting terms per fragment on a mobile-class GPU for a look the
  // hemisphere already delivers. Everything here is unlit MeshBasic — the
  // DESIGN_SYSTEM emissive allowance — so the fittings cost geometry only.
  /** The hot tube. Unlit basic, so it reads as emissive against dim concrete. */
  const stripGeo = new BoxGeometry(0.55, 0.14, 3.2);
  // Near-white with a sodium bias: a lamp's own filament always clips brighter
  // than the light it throws, so this sits above the hemiSky tone (0xffb46b)
  // that the concrete is lit in.
  const stripMat = applyEarthCurve(new MeshBasicMaterial({ color: 0xffe7c2 }));
  stripMat.fog = true;
  /** Fixture housing: a dark shroud around the tube, so it reads as a FITTING. */
  const lampBoxGeo = new BoxGeometry(0.78, 0.2, 3.5);
  const lampBoxMat = plasticMaterial({ color: shade(0xc7b6a4, 0.32), name: "tunnelLampBox" });
  /**
   * HALO: a downward-facing additive quad hanging just under each fitting. This
   * is what sells "the ceiling is emitting" — a bare emissive box in fog reads
   * as a bright sticker, while a soft bloom around it reads as a lamp. Additive
   * + depthWrite off + fogged, exactly like the mouth-glow quads below.
   */
  const haloGeo = new PlaneGeometry(2.6, 5.0);
  haloGeo.rotateX(-Math.PI / 2);
  const haloMat = applyEarthCurve(new MeshBasicMaterial({ color: 0xffb46b }));
  haloMat.transparent = true;
  haloMat.opacity = 0.22;
  haloMat.blending = AdditiveBlending;
  haloMat.depthWrite = false;
  haloMat.fog = true;
  const glowGeo = new PlaneGeometry(2 * TUNNEL.wallX + 2, TUNNEL.crownY + 2);
  const glowMat = applyEarthCurve(new MeshBasicMaterial({ color: 0xff9d3c }));
  glowMat.transparent = true;
  glowMat.opacity = 0.5;
  glowMat.blending = AdditiveBlending;
  glowMat.depthWrite = false;
  // Fogged like everything else: without this the mouth glowed through 400 m
  // of tube because the biome's fog far is only 72 m.
  glowMat.fog = true;
  // Wall panel seams + the walkway kerb along the wall base.
  //
  // The seams are FLAT PANELS lying on the wall, not standing boxes. The first
  // pass extruded a thin box off the wall, and because its faces caught the key
  // light head-on while the tube behind it was a fogged vertex-colour ramp, the
  // "darker seam" rendered BRIGHTER than the wall — a row of pale posts down
  // the tunnel. A coplanar quad shares the wall's orientation, so it takes the
  // same light and its darker albedo actually reads as a recess. DoubleSide so
  // one geometry serves both walls.
  const seamGeo = new PlaneGeometry(0.12, TUNNEL.springY - 0.15);
  seamGeo.rotateY(Math.PI / 2);
  seamGeo.translate(0, (TUNNEL.springY - 0.15) / 2, 0);
  const seamMat = plasticMaterial({ color: shade(0xc7b6a4, 0.72), name: "tunnelSeam" });
  seamMat.side = DoubleSide;
  const walkGeo = new BoxGeometry(TUNNEL.walkwayW, TUNNEL.walkwayH, CHUNK_LENGTH, 1, 1, 10);
  const walkMat = plasticMaterial({ color: shade(0xc7b6a4, 1.06), name: "tunnelWalkway" });
  // Alcoves: a dark recessed panel on the wall (same coplanar-quad trick as the
  // seams — a standing box would catch the key light and read BRIGHTER than the
  // wall) plus a chunky service cabinet standing inside it.
  const alcoveGeo = new PlaneGeometry(TUNNEL.alcoveW, TUNNEL.alcoveH);
  alcoveGeo.rotateY(Math.PI / 2);
  alcoveGeo.translate(0, TUNNEL.alcoveH / 2 + 0.1, 0);
  const alcoveMat = plasticMaterial({ color: shade(0xc7b6a4, 0.45), name: "tunnelAlcove" });
  alcoveMat.side = DoubleSide;
  const cabinetGeo = new BoxGeometry(0.55, 1.5, 1.1);
  cabinetGeo.translate(0, 0.75, 0);
  const cabinetMat = plasticMaterial({ color: 0x9c6220, name: "tunnelCabinet" });
  // Sign gantry: a beam across the crown with a sign plate hung under it. Both
  // are silhouettes — no legible typography at speed, per DESIGN_SYSTEM.
  const gantryGeo = new BoxGeometry(2 * TUNNEL.wallX - 0.6, 0.34, 0.5);
  const gantryMat = plasticMaterial({ color: shade(0xc7b6a4, 0.62), name: "tunnelGantry" });
  const signGeo = new BoxGeometry(4.2, 1.3, 0.16);
  const signMat = plasticMaterial({ color: 0x2c5240, name: "tunnelSign" });
  const owned: (BufferGeometry | Material)[] = [
    tubeGeo,
    tubeMat,
    stripGeo,
    stripMat,
    lampBoxGeo,
    lampBoxMat,
    haloGeo,
    haloMat,
    glowGeo,
    glowMat,
    seamGeo,
    seamMat,
    walkGeo,
    walkMat,
    alcoveGeo,
    alcoveMat,
    cabinetGeo,
    cabinetMat,
    gantryGeo,
    gantryMat,
    signGeo,
    signMat,
  ];
  const fieldSet = new FieldSet();

  const factory: ChunkFactory = (group, F) => {
    const tube = new Mesh(tubeGeo, tubeMat);
    tube.frustumCulled = false;
    group.add(tube);
    // Nothing in the tunnel casts: the sun is OUTSIDE the tube, so a tube or a
    // ceiling strip in the shadow map would only black the deck out (the rig is
    // deliberately left alone inside the tunnel — see the module note).
    // Light fittings: housing, tube, halo — drawn back-to-front so the additive
    // halo composites over the concrete it is meant to be washing.
    const lampBoxes = F.field(
      lampBoxGeo,
      lampBoxMat,
      TUNNEL.stripsPerRow * 2,
      false,
      null,
      false,
    );
    const strips = F.field(stripGeo, stripMat, TUNNEL.stripsPerRow * 2, false, null, false);
    const halos = F.field(haloGeo, haloMat, TUNNEL.stripsPerRow * 2, false, null, false);
    halos.shared.mesh.renderOrder = 2;
    const seams = F.field(seamGeo, seamMat, TUNNEL.seamsPerSide * 2);
    // Alcove recesses + their cabinets. Nothing in the tube casts (the sun is
    // outside it), so both fields opt out of the shadow pass.
    const alcoves = F.field(alcoveGeo, alcoveMat, TUNNEL.alcovesPerSide * 2, false, null, false);
    const cabinets = F.field(cabinetGeo, cabinetMat, TUNNEL.alcovesPerSide * 2, false, null, false);
    // Overhead sign gantry — one per chunk slot, shown on every 4th local
    // chunk so the rhythm is stable and never lands on a mouth.
    const gantry = new Mesh(gantryGeo, gantryMat);
    gantry.position.set(0, TUNNEL.crownY - 1.9, CHUNK_LENGTH / 2);
    gantry.frustumCulled = false;
    const sign = new Mesh(signGeo, signMat);
    sign.position.set(0, TUNNEL.crownY - 2.75, CHUNK_LENGTH / 2);
    sign.frustumCulled = false;
    group.add(gantry, sign);
    for (const sideSign of [-1, 1]) {
      const walk = new Mesh(walkGeo, walkMat);
      walk.position.set(sideSign * TUNNEL.walkwayX, TUNNEL.walkwayH / 2, CHUNK_LENGTH / 2);
      walk.frustumCulled = false;
      group.add(walk);
    }
    const glowIn = new Mesh(glowGeo, glowMat);
    glowIn.position.set(0, (TUNNEL.crownY + 2) / 2 - 1, CHUNK_LENGTH - 0.4);
    glowIn.frustumCulled = false;
    const glowOut = new Mesh(glowGeo, glowMat);
    glowOut.position.set(0, (TUNNEL.crownY + 2) / 2 - 1, 0.4);
    glowOut.frustumCulled = false;
    group.add(glowIn, glowOut);

    return (slice: number): void => {
      const local = localChunkIndex(slice);
      glowIn.visible = local === 0; // entry mouth
      glowOut.visible = local === CHUNKS_PER_BIOME - 1; // exit mouth
      // Light fittings on a fixed 10 m pitch (CHUNK_LENGTH / stripsPerRow), two
      // rows. The halo hangs a little under the tube so its additive wash lands
      // on the crown and the upper wall rather than inside the housing.
      strips.reset();
      lampBoxes.reset();
      halos.reset();
      const lampY = TUNNEL.crownY - TUNNEL.lampDrop;
      for (let i = 0; i < TUNNEL.stripsPerRow; i++) {
        const z = (i + 0.5) * (CHUNK_LENGTH / TUNNEL.stripsPerRow);
        for (const sideSign of [-1, 1]) {
          const x = sideSign * TUNNEL.lampX;
          lampBoxes.add(x, lampY + 0.08, z, 0, 1);
          strips.add(x, lampY, z, 0, 1);
          halos.add(x, lampY - 0.22, z, 0, 1);
        }
      }
      strips.flush();
      lampBoxes.flush();
      halos.flush();

      // Panel seams: a fixed pitch (a structural rhythm, not a random scatter),
      // pressed a few cm proud of the wall so they never z-fight the tube.
      seams.reset();
      for (let i = 0; i < TUNNEL.seamsPerSide; i++) {
        const z = (i + 0.5) * (CHUNK_LENGTH / TUNNEL.seamsPerSide);
        seams.add(-(TUNNEL.wallX - 0.03), 0, z, 0, 1);
        seams.add(TUNNEL.wallX - 0.03, 0, z, 0, 1);
      }
      seams.flush();

      // Alcoves: a recess with a service cabinet, on a fixed half-chunk pitch
      // offset from the seam rhythm so the two never coincide.
      alcoves.reset();
      cabinets.reset();
      for (let i = 0; i < TUNNEL.alcovesPerSide; i++) {
        const z = (i + 0.25) * (CHUNK_LENGTH / TUNNEL.alcovesPerSide);
        for (const sideSign of [-1, 1]) {
          alcoves.add(sideSign * (TUNNEL.wallX - 0.04), 0, z, 0, 1);
          if (rnd(slice, 900 + i * 3 + (sideSign > 0 ? 0 : 1)) < 0.6) {
            cabinets.add(sideSign * (TUNNEL.wallX - 0.35), TUNNEL.walkwayH, z, 0, 1);
          }
        }
      }
      alcoves.flush();
      cabinets.flush();

      const hasGantry = local % TUNNEL.gantryEveryChunks === 2;
      gantry.visible = hasGantry;
      sign.visible = hasGantry;
    };
  };

  return {
    chunks: buildChunks(factory, fieldSet),
    fields: fieldSet,
    backdrop: null, // no sky, no skyline inside the tube
    dispose(): void {
      for (const o of owned) o.dispose();
      fieldSet.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// Sky gradient (screen-space background, blended every frame)
// ---------------------------------------------------------------------------

const SKY_ROWS = 64;
/** Screen-space v of the horizon (see `SkyGradient.set`). */
const SKY_HORIZON_V = 0.45;

/**
 * A 4 × SKY_ROWS vertical gradient used as `scene.background`. three renders a
 * non-cube background texture as a screen-space quad, so this IS the sky
 * gradient (zenith at the top row, horizon at the bottom). The pixels are
 * rewritten in place every frame from the blended zenith/horizon colours, which
 * is what makes the sky crossfade across a seam.
 */
class SkyGradient {
  readonly texture: DataTexture;
  private readonly data: Uint8Array;
  private readonly zenith = new Color();
  private readonly horizon = new Color();
  private readonly tmp = new Color();

  constructor() {
    this.data = new Uint8Array(4 * SKY_ROWS * 4);
    this.texture = new DataTexture(this.data, 4, SKY_ROWS);
    this.texture.colorSpace = SRGBColorSpace;
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
    this.texture.needsUpdate = true;
  }

  /** Write a zenith->horizon ramp. Colours are sRGB hex-space (background). */
  set(zenithHex: number, horizonHex: number, blendHex: number | null, t: number): void {
    this.zenith.setHex(zenithHex, SRGBColorSpace);
    this.horizon.setHex(horizonHex, SRGBColorSpace);
    if (blendHex !== null && t > 0) {
      this.tmp.setHex(blendHex, SRGBColorSpace);
    }
    for (let row = 0; row < SKY_ROWS; row++) {
      // Row 0 is the BOTTOM of the screen quad (v = 0). The camera looks
      // slightly DOWN, so the real horizon sits around 45% of the frame
      // height: everything below that is covered by the road/ground anyway,
      // and the zenith->horizon ramp has to spend its whole range ABOVE it or
      // the visible sky is one flat saturated blue with a hard band in it.
      const v = row / (SKY_ROWS - 1);
      const k = smoothstep01((v - SKY_HORIZON_V) / (1 - SKY_HORIZON_V));
      const r = lerp(this.horizon.r, this.zenith.r, k);
      const g = lerp(this.horizon.g, this.zenith.g, k);
      const b = lerp(this.horizon.b, this.zenith.b, k);
      for (let col = 0; col < 4; col++) {
        const o = (row * 4 + col) * 4;
        this.data[o] = Math.round(Math.min(1, Math.max(0, r)) * 255);
        this.data[o + 1] = Math.round(Math.min(1, Math.max(0, g)) * 255);
        this.data[o + 2] = Math.round(Math.min(1, Math.max(0, b)) * 255);
        this.data[o + 3] = 255;
      }
    }
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface BiomeSystem {
  readonly group: Group;
  readonly fog: Fog;
  /** Per-frame update. `distance` = player.distance (m travelled). */
  update(distance: number): void;
  /** Current blend state — exposed for debug/QA. */
  readonly state: Readonly<BiomeBlend>;
  /**
   * PIPELINE WARM-UP (2026-08-01, rewritten 2026-08-02). Pose every biome's
   * whole content set in front of the camera for ONE pass.
   *
   * Why: a biome's materials only enter the frame when the player reaches that
   * biome, and the first frame that draws a new material compiles its WebGPU
   * pipeline — a measured 20-90 ms stall at the seam into forest/fields/tunnel
   * on the first cycle. main.ts calls this during LOADING, compiles, then
   * restores; after that every biome transition is allocation- and
   * compile-free.
   *
   * ONE PASS IS ENOUGH since the props became one InstancedMesh per (biome,
   * archetype): the SLOT axis that used to need eight passes no longer exists,
   * and the CHUNK_COUNT recycled slots cover all CHUNKS_PER_BIOME local slices
   * simultaneously, so every slice-dependent variant is on screen at once.
   *
   * The predecessor left 128-138 pipeline compiles spread through a run, each
   * one a 60-170 ms frame inside `renderer.render` — measured with a
   * GPUDevice.createRenderPipeline probe (perfbench.html), which attributed
   * them to exactly two chunk slots per (biome, archetype): its loop ran to
   * CHUNKS_PER_BIOME (8) over an array of CHUNK_COUNT (10) slots. Measured
   * after this rewrite: 0 over 6271 m (2.6 six-biome laps, 107 kicks).
   */
  warmup(): BiomeWarmup;
  dispose(): void;
}

/**
 * Multi-pass biome warm-up handle. Mirrors {@link VehicleWarmup}: the caller
 * runs `passes` iterations of `beginPass(p)` + `compileAsync` + one real frame
 * (the real frame is what compiles the SHADOW-pass pipelines), then `restore`.
 */
export interface BiomeWarmup {
  /** Number of passes the caller must render. */
  readonly passes: number;
  /** Pose every biome's every local slice in front of the camera (idempotent). */
  beginPass(p: number): void;
  /** Hide everything again and hand the chunks back to `update`. */
  restore(): void;
}

// Reusable temporaries (zero per-frame allocation).
const _pose: ChunkPose = { x: 0, z: 0, yaw: 0 };
const _blend: BiomeBlend = {
  index: 0,
  id: "city_downtown",
  next: "bridge_suspension",
  local: 0,
  t: 0,
  cycle: 0,
};

/**
 * Create the six-biome system. Loads the env GLBs async; any asset that fails
 * simply doesn't appear (the rest of the biome still builds).
 *
 * @param road  the road world — owns the shared asphalt/rail materials + lights
 * @param scene the scene (this system owns `scene.background` and `scene.fog`)
 * @param seed  the run seed, so placement is deterministic per run
 */
export async function createBiomeSystem(
  _registry: unknown,
  road: RoadWorld,
  scene: Scene,
  seed = 1,
): Promise<BiomeSystem> {
  RUN_SEED = (seed | 0) || 1;
  const group = new Group();
  group.name = "biomes";

  const templates = await loadEnvTemplates();

  // --- Per-biome content ---------------------------------------------------
  const downtown = buildCityContent(templates, {
    heroKeys: ["buildingCream", "buildingBrick", "buildingGlass"],
    // Far rank = the downtown backdrop's own haze blues, so the silhouette
    // masses read as the near end of the skyline card rather than as buildings.
    farTones: [0x7c96b2, 0x8aa2bd, 0x7189a6, 0x94aac2],
    farHeight: [22, 54],
    midChance: 0.55,
    waterTowers: true,
    treesPerSide: 0,
    treeChance: 0,
    furnitureTones: [0x457b9d, 0x2a9d8f, 0x8a8f96, 0xd8cbb0],
    bollardTone: 0x6f757c,
    pavement: BIOMES.city_downtown.ground,
  });
  downtown.backdrop = downtownBackdrop();
  const autumn = buildCityContent(templates, {
    heroKeys: ["brownstone"],
    farTones: [0xc3ad96, 0xb49c85, 0xd0bba4, 0xc98f52],
    farHeight: [12, 26],
    midChance: 0.35,
    waterTowers: false,
    treeKey: "autumnTree",
    treesPerSide: 1,
    treeChance: 0.6,
    canopyTones: [0xe8862f, 0xf2b035, 0xc4522e],
    furnitureTones: [0x8d5b45, 0x6b4a36, 0xa06b4e, 0xb9b3a6],
    bollardTone: 0x6a6058,
    leafTones: [0xe8862f, 0xc4522e, 0xf2b035],
    pavement: BIOMES.city_autumn.ground,
  });
  autumn.backdrop = autumnBackdrop();

  const content: Record<BiomeId, BiomeContent> = {
    city_downtown: downtown,
    bridge_suspension: buildBridgeContent(templates),
    forest_pine: buildForestContent(templates),
    fields_wheat: buildFieldsContent(templates),
    tunnel: buildTunnelContent(),
    city_autumn: autumn,
  };

  // --- Global ground plane -------------------------------------------------
  // ONE plane centred on the player (the player never leaves world z = 0), so
  // the ground colour is a continuous global that lerps across a seam instead
  // of drawing a hard colour line 50 m ahead. It doubles as the bridge's water.
  // 96x96 (was a single quad): the bend is applied PER VERTEX, so the ground
  // plane needs enough subdivision to follow the parabola. 14.6 m cells leave a
  // worst-case 2.4 cm chord error, always DOWNWARD (earthCurve.ts), so the
  // ground can only sink away from the props standing on it, never poke
  // through them. ~18k triangles on one draw call, no per-frame CPU cost.
  const groundGeo = new PlaneGeometry(1400, 1400, 96, 96);
  groundGeo.rotateX(-Math.PI / 2);
  const groundMat = plasticMaterial({ color: BIOMES.city_downtown.ground, name: "biomeGround" });
  const ground = new Mesh(groundGeo, groundMat);
  ground.receiveShadow = true;
  ground.frustumCulled = false;
  ground.position.y = BIOMES.city_downtown.groundY;
  group.add(ground);

  // --- Horizon haze band ---------------------------------------------------
  const haze = buildHorizonHaze();
  group.add(haze.mesh);

  // --- Backdrops -----------------------------------------------------------
  const backdropRoot = new Group();
  group.add(backdropRoot);
  for (const id of BIOME_ORDER) {
    const b = content[id].backdrop;
    if (b) {
      backdropRoot.add(b.group);
      b.setWeight(id === "city_downtown" ? 1 : 0);
    }
  }

  // --- Chunk groups --------------------------------------------------------
  // THE SHARED PROP ROOT (2026-08-02 program-count pass). Every biome's
  // instanced prop meshes hang off this ONE group; its position carries the
  // whole player-distance part of every chunk's pose (see the SharedField note).
  // The chunk groups themselves still exist and are still posed per frame —
  // they carry the NON-instanced per-chunk meshes (kerbs, rails, the tunnel
  // tube, the bridge cables), which share a program per material and were never
  // part of the program-count problem.
  const propRoot = new Group();
  propRoot.name = "biomeProps";
  group.add(propRoot);
  for (let i = 0; i < CHUNK_COUNT; i++) {
    updateSlotMatrix(i, initialAnchor(i, CHUNK_LENGTH, CHUNKS_BEHIND));
  }
  for (const id of BIOME_ORDER) {
    content[id].fields.attach(propRoot);
    for (const chunk of content[id].chunks) {
      group.add(chunk.group);
      chunkPose(-chunk.anchorD - CHUNK_LENGTH, 0, CHUNK_LENGTH, _pose);
      chunk.group.position.set(_pose.x, 0, _pose.z);
      chunk.group.rotation.y = _pose.yaw;
      chunk.populate(Math.round(chunk.anchorD / CHUNK_LENGTH));
      chunk.setVisible(false);
    }
  }
  /** Which biome each recycled slot currently shows. */
  const slotBiome: BiomeId[] = new Array(CHUNK_COUNT).fill("city_downtown");
  for (let i = 0; i < CHUNK_COUNT; i++) {
    const anchorD = content.city_downtown.chunks[i].anchorD;
    const id = biomeIdAt(anchorD);
    slotBiome[i] = id;
    content[id].chunks[i].populate(Math.round(anchorD / CHUNK_LENGTH));
    content[id].chunks[i].setVisible(true);
  }
  /** Per-biome bitmask of the slots it currently owns — rebuilt every frame. */
  const visMask = new Int32Array(BIOME_COUNT);
  const shadowMask = new Int32Array(BIOME_COUNT);
  /** Re-derive each slot's biome ownership and hand the fields their masks. */
  const applyMasks = (): void => {
    visMask.fill(0);
    shadowMask.fill(0);
    for (let i = 0; i < CHUNK_COUNT; i++) {
      visMask[BIOME_ORDER.indexOf(slotBiome[i])] |= 1 << i;
    }
    shadowMask.set(visMask);
    for (let b = 0; b < BIOME_COUNT; b++) {
      content[BIOME_ORDER[b]].fields.apply(visMask[b], shadowMask[b]);
    }
  };
  applyMasks();

  // --- Global state --------------------------------------------------------
  const first = BIOMES.city_downtown;
  const fog = new Fog(first.fog, first.fogNear, first.fogFar);
  scene.fogNode = saturatingFogNode(fog);
  const sky = new SkyGradient();
  sky.set(first.skyZenith, first.skyHorizon, null, 0);
  scene.background = sky.texture;

  const tmpA = new Color();
  const tmpB = new Color();
  const cFog = new Color();
  const cHemiSky = new Color();
  const cHemiGround = new Color();
  const cSun = new Color();
  const cEnvSky = new Color();
  const cEnvGround = new Color();
  const cAsphalt = new Color();
  const cRail = new Color();
  const cGround = new Color();
  /** Lerp two biome hexes into `out`. */
  const mix = (out: Color, a: number, b: number, t: number): Color => {
    tmpA.setHex(a, SRGBColorSpace);
    tmpB.setHex(b, SRGBColorSpace);
    return out.copy(tmpA).lerp(tmpB, t);
  };
  /** Lerp a hex pair back to an sRGB hex (for the sky data texture). */
  const mixHex = (a: number, b: number, t: number): number => {
    tmpA.setHex(a, SRGBColorSpace);
    tmpB.setHex(b, SRGBColorSpace);
    return tmpA.lerp(tmpB, t).getHex(SRGBColorSpace);
  };

  return {
    group,
    fog,
    state: _blend,
    warmup(): BiomeWarmup {
      // ONE PASS (2026-08-02 program-count pass). With one InstancedMesh per
      // (biome, archetype) the slot axis of the old warm-up is gone: there is
      // nothing per-slot left to compile. What remains is the SLICE axis — a
      // chunk's contents are a function of its index inside the biome — and the
      // CHUNK_COUNT slots cover all CHUNKS_PER_BIOME slices simultaneously, so
      // one configuration poses every variant there is.
      //
      // `warmFill` closes the last hole. Several fields only populate behind a
      // `rnd(slice, …)` roll (the water towers, the mown circles, the barns, the
      // tunnel's service cabinets), and the run reaches slice numbers this pass
      // never sees — so any archetype that came out empty here is given one
      // instance, and the whole set draws in both passes.
      const shown: ChunkContent[] = [];
      const ALL = (1 << CHUNK_COUNT) - 1;
      const hideShown = (): void => {
        for (const chunk of shown) chunk.setVisible(false);
        shown.length = 0;
      };
      return {
        passes: 1,
        beginPass(): void {
          hideShown();
          propRoot.position.set(0, 0, 0);
          for (const id of BIOME_ORDER) {
            for (let i = 0; i < CHUNK_COUNT; i++) {
              const chunk = content[id].chunks[i];
              chunk.populate(i % CHUNKS_PER_BIOME);
              chunk.group.position.set(0, 0, -CHUNK_LENGTH);
              chunk.group.rotation.y = 0;
              chunk.setVisible(true);
              chunk.setCastShadow(true);
              shown.push(chunk);
            }
            content[id].fields.warmFill();
            content[id].fields.apply(ALL, ALL);
          }
          for (const id of BIOME_ORDER) content[id].backdrop?.setWeight(1);
        },
        restore(): void {
          hideShown();
          for (const id of BIOME_ORDER) content[id].backdrop?.setWeight(0);
          // PUT THE AUTHORED WORLD BACK. The pass above overwrote every slot's
          // instances with warm-up slices, and `update` only repopulates a slot
          // when it RECYCLES — so without this the first 400 m of the run would
          // draw the warm-up's placement instead of its own. (The pre-2026-08-02
          // warm-up had exactly that bug: its last pass left slot i holding
          // slice i+7.)
          for (const id of BIOME_ORDER) {
            for (const chunk of content[id].chunks) {
              chunk.populate(Math.round(chunk.anchorD / CHUNK_LENGTH));
            }
          }
          applyMasks();
          // The next `update` re-derives visibility, pose and backdrop weights
          // from the travel distance, so nothing else needs restoring.
        },
      };
    },
    update(distance: number): void {
      const blend = biomeBlendAt(distance, _blend);
      const A = BIOMES[blend.id];
      const B = BIOMES[blend.next];
      const t = blend.t;

      // --- Global look (continuous across the seam chunk) -----------------
      fog.color.copy(mix(cFog, A.fog, B.fog, t));
      fog.near = lerp(A.fogNear, B.fogNear, t);
      fog.far = lerp(A.fogFar, B.fogFar, t);
      // The horizon haze band rides the SAME blended fog colour/far, so it
      // transitions with everything else at a seam.
      haze.set(fog.color, fog.far);
      road.setLightTint(
        mix(cHemiSky, A.hemiSky, B.hemiSky, t),
        mix(cHemiGround, A.hemiGround, B.hemiGround, t),
        mix(cSun, A.sun, B.sun, t),
      );
      // LIGHT PROFILE hook (2026-08-02): how MUCH of each light family, as
      // opposed to what colour it is. Lerped on the same `t`, so the tunnel's
      // sun fades out and its sodium fill fades in across the seam chunk
      // instead of switching. Omitted multipliers default to 1, which is why
      // every outdoor->outdoor seam blends 1 to 1 and changes nothing.
      road.setLightProfile(
        lerp(A.sunMul ?? 1, B.sunMul ?? 1, t),
        lerp(A.hemiMul ?? 1, B.hemiMul ?? 1, t),
        lerp(A.envMul ?? 1, B.envMul ?? 1, t),
      );
      // CHEAP GI hook: the gradient environment's two hemispheres. Blended on
      // the same `t` as everything else, so a candy vehicle's clearcoat sheen
      // crossfades from (say) wheat-warm to pine-cool across the seam chunk.
      road.setEnvTint(
        mix(cEnvSky, A.envSky, B.envSky, t),
        mix(cEnvGround, A.envGround, B.envGround, t),
      );
      // ROAD/RAIL STYLE hook: re-tint the SHARED material instances (never per
      // chunk) — see world.ts setRoadTint.
      road.setRoadTint(
        mix(cAsphalt, A.asphalt, B.asphalt, t),
        mix(cRail, A.rail, B.rail, t),
      );
      groundMat.color.copy(mix(cGround, A.ground, B.ground, t));
      ground.position.y = lerp(A.groundY, B.groundY, t);
      sky.set(mixHex(A.skyZenith, B.skyZenith, t), mixHex(A.skyHorizon, B.skyHorizon, t), null, 0);

      // Backdrops: crossfade the two biomes' silhouette layers, and park them
      // ahead of the (yawed) camera so a bend never slides them out of frame.
      const camYaw = -Math.atan(curveTangent(distance));
      backdropRoot.position.set(-Math.sin(camYaw) * BACKDROP_DIST, 0, -Math.cos(camYaw) * BACKDROP_DIST);
      backdropRoot.rotation.y = camYaw;
      for (const id of BIOME_ORDER) {
        const b = content[id].backdrop;
        if (!b) continue;
        const w = id === blend.id ? 1 - t : id === blend.next ? t : 0;
        b.setWeight(w);
      }

      // --- Per-chunk content (discrete, swapped beyond the fog horizon) ---
      // THE PROP ROOT carries the whole player-distance half of every chunk's
      // pose, for every biome at once — one write per frame instead of 60 group
      // transforms. The per-slot half lives in the instance matrices and only
      // moves when a slot recycles. See the SharedField module note.
      propRoot.position.set(-curveX(distance), 0, distance);
      visMask.fill(0);
      shadowMask.fill(0);
      for (let i = 0; i < CHUNK_COUNT; i++) {
        const anchorD = content.city_downtown.chunks[i].anchorD;
        const newAnchorD = recycleAnchor(anchorD, distance, CHUNK_LENGTH, CHUNK_COUNT, CHUNKS_BEHIND);
        const recycled = newAnchorD !== anchorD;
        if (recycled) {
          for (const id of BIOME_ORDER) content[id].chunks[i].anchorD = newAnchorD;
          // The slot's rigid transform is a pure function of its anchor, so it
          // is re-derived exactly here — BEFORE the repopulate below writes the
          // new instances through it.
          updateSlotMatrix(i, newAnchorD);
        }
        const slice = Math.round(newAnchorD / CHUNK_LENGTH);
        const targetIdx = biomeIndexAt(newAnchorD);
        const target = BIOME_ORDER[targetIdx];

        const aheadEdgeZ = distance - newAnchorD - CHUNK_LENGTH;
        chunkPose(aheadEdgeZ, distance, CHUNK_LENGTH, _pose);
        for (const id of BIOME_ORDER) {
          const g = content[id].chunks[i].group;
          g.position.set(_pose.x, 0, _pose.z);
          g.rotation.y = _pose.yaw;
        }

        if (slotBiome[i] !== target) {
          content[slotBiome[i]].chunks[i].setVisible(false);
          content[target].chunks[i].populate(slice);
          slotBiome[i] = target;
        } else if (recycled) {
          content[target].chunks[i].populate(slice);
        }

        // Submit only what is inside the fog horizon; everything else is
        // fully fogged (or behind the camera) and would be pure waste.
        const nearEdgeZ = aheadEdgeZ + CHUNK_LENGTH;
        const inWindow =
          nearEdgeZ > -(fog.far + SHOW_AHEAD_MARGIN_M) && aheadEdgeZ < SHOW_BEHIND_M;
        content[target].chunks[i].setVisible(inWindow);
        // Shadow casters are culled far tighter than the colour pass: the sun's
        // ortho box only reaches SHADOW_AHEAD_M, and a caster outside it
        // contributes nothing but shadow-pass draw calls.
        const casts = inWindow && nearEdgeZ > -SHADOW_AHEAD_M && aheadEdgeZ < SHADOW_BEHIND_M;
        content[target].chunks[i].setCastShadow(casts);
        // The same two windows, for the instanced props: `apply` compacts the
        // visible slots into each shared mesh and lays the casters down first.
        if (inWindow) {
          visMask[targetIdx] |= 1 << i;
          if (casts) shadowMask[targetIdx] |= 1 << i;
        }
      }
      for (let b = 0; b < BIOME_COUNT; b++) {
        content[BIOME_ORDER[b]].fields.apply(visMask[b], shadowMask[b]);
      }
    },
    dispose(): void {
      for (const id of BIOME_ORDER) {
        content[id].dispose();
        content[id].backdrop?.dispose();
      }
      groundGeo.dispose();
      groundMat.dispose();
      haze.dispose();
      sky.dispose();
      for (const key of Object.keys(templates) as EnvKey[]) {
        const tpl = templates[key];
        if (!tpl) continue;
        tpl.geometry.dispose();
        tpl.material.dispose();
      }
    },
  };
}
